import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  rectIntersection,
  pointerWithin
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";

import {
  Card,
  Button,
  Space,
  Tag,
  Tooltip,
  Switch,
  Input,
  Typography,
  Empty,
  message,
  Spin,
  Modal,
  Checkbox,
  Badge,
  Segmented,
  Select,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  HolderOutlined,
  CaretRightOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  UnorderedListOutlined,
  SearchOutlined,
  TagOutlined,
  CompressOutlined,
  ExpandOutlined,
  EditOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Toast } from "../../../ui";
import SynchronizationService from "../../../../services/synchronizationService";
import { useTheme } from "../../../../contexts/ThemeContext";

import {
  TIPO_DRE_PAGAMENTO_DEFAULT,
  TIPO_DRE_PAGAMENTO_OPTIONS,
  getTipoDrePagamentoDescricao,
  getTipoDrePagamentoExemplo,
  normalizeTipoDrePagamento,
} from "./tipoDrePagamento";
import { DroppableList } from "./DroppableList";
import { SortableCard } from "./SortableCard";
import DefaultFinancialCategoriesConfig from "./DefaultFinancialCategoriesConfig";
import { CategoriasComLancamentoSemDre } from "./CategoriasComLancamentoSemDre";

const { Text } = Typography;

const ZERO_GUID = "00000000-0000-0000-0000-000000000000";

// Renderiza o exemplo de DRE (ícone "i" ao lado da explicação da regra de pagamento)
// como um mini-demonstrativo, para o usuário ver como ficaria na prática.
// O Tooltip usa fundo branco, então as cores aqui são fixas (escuras).
function renderExemploDre(exemplo) {
  if (!exemplo) return null;
  return (
    <div style={{ maxWidth: 280, color: "#1f1f1f", padding: "2px 0" }}>
      <div style={{ fontSize: 12, marginBottom: 8, color: "#595959" }}>
        {exemplo.cenario}
      </div>
      <div
        style={{
          border: "1px solid #f0f0f0",
          borderRadius: 6,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#595959",
            padding: "4px 8px",
            backgroundColor: "#fafafa",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          Como aparece no DRE
        </div>
        {exemplo.linhas.map((linha, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 16,
              padding: "4px 8px",
              fontSize: 13,
              fontWeight: linha.total ? 700 : 400,
              borderTop: linha.total ? "1px solid #f0f0f0" : "none",
            }}
          >
            <span>{linha.label}</span>
            <span style={{ whiteSpace: "nowrap" }}>{linha.valor}</span>
          </div>
        ))}
      </div>
      {exemplo.nota ? (
        <div style={{ fontSize: 12, marginTop: 8, color: "#595959" }}>
          {exemplo.nota}
        </div>
      ) : null}
    </div>
  );
}

function normalizeCategoryCode(value) {
  if (value === null || value === undefined) return "";
  return String(value)
    .normalize("NFKC")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\s+/g, "")
    .toUpperCase();
}

const CatSelect = memo(
  function CatSelect({
    sub,
    cats,
    theme,
    bulkSelectedCatIds,
    setBulkSelectedCatIds,
  }) {
    const [searchTerm, setSearchTerm] = useState("");
    const normalizeCode = useCallback((value) => {
      if (value === null || value === undefined) return "";
      return String(value)
        .normalize("NFKC")
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        .replace(/\s+/g, "")
        .toUpperCase();
    }, []);
    const normalizeDescription = useCallback((value) => {
      if (value === null || value === undefined) return "";
      return String(value)
        .normalize("NFKC")
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        .trim()
        .toUpperCase();
    }, []);
    const getDisplayKey = useCallback(
      (cat) => {
        const normalizedDesc = normalizeDescription(cat?.descricao);
        const match = normalizedDesc.match(/^([0-9]+(?:\.[0-9]+)*)\s*-/);
        return match?.[1] || normalizedDesc;
      },
      [normalizeDescription],
    );

    const isAvailableForCurrentSub = useCallback(
      (cat) => cat.syncDREId === sub.id || !cat.syncDREId || cat.syncDREId === ZERO_GUID,
      [sub.id],
    );

    const treeState = useMemo(() => {
      const sortByDescricao = (a, b) =>
        String(a.descricao || "").localeCompare(String(b.descricao || ""), "pt-BR", {
          sensitivity: "base",
        });

      const guidToDisplayKey = new Map();
      cats.forEach((cat) => {
        const guid = normalizeCode(cat.codCategoria);
        if (!guid) return;
        guidToDisplayKey.set(guid, getDisplayKey(cat));
      });

      const groupsByDisplayKey = new Map();
      cats.forEach((cat) => {
        const displayKey = getDisplayKey(cat);
        if (!displayKey) return;
        if (!groupsByDisplayKey.has(displayKey)) {
          groupsByDisplayKey.set(displayKey, []);
        }
        groupsByDisplayKey.get(displayKey).push(cat);
      });

      const byDisplayKey = new Map();
      let hasParentConflict = false;

      groupsByDisplayKey.forEach((group, displayKey) => {
        const availableItems = group.filter(isAvailableForCurrentSub);
        const memberIds = group
          .flatMap((cat) =>
            isAvailableForCurrentSub(cat) && cat.id ? [cat.id] : [],
          )
          .filter(Boolean);
        const uniqueMemberIds = Array.from(new Set(memberIds));

        const parentDisplayCandidates = Array.from(
          new Set(
            group
              .map((cat) => {
                const parentGuid = normalizeCode(cat.codCategoriaSup);
                if (!parentGuid) return null;
                return guidToDisplayKey.get(parentGuid) || null;
              })
              .filter(Boolean),
          ),
        );

        let parentDisplayKey = null;
        if (parentDisplayCandidates.length === 1) {
          parentDisplayKey = parentDisplayCandidates[0];
        } else if (parentDisplayCandidates.length > 1) {
          hasParentConflict = true;
        }

        const linkedToCurrentSub = group.some((cat) => cat.syncDREId === sub.id);
        const representative =
          group.find((cat) => cat.syncDREId === sub.id) ||
          availableItems.find((cat) => !!normalizeCode(cat.codCategoriaSup)) ||
          group[0];
        const descricao =
          representative?.descricao ||
          group.find((cat) => String(cat.descricao || "").trim())?.descricao ||
          displayKey;

        byDisplayKey.set(displayKey, {
          key: displayKey,
          cat: {
            ...representative,
            codCategoria: displayKey,
            codCategoriaSup: parentDisplayKey,
            descricao,
            memberIds: uniqueMemberIds,
            linkedToCurrentSub,
          },
          children: [],
        });
      });

      const roots = [];
      let linkedEdges = 0;
      byDisplayKey.forEach((node) => {
        const parentKey = node.cat.codCategoriaSup;
        if (parentKey && byDisplayKey.has(parentKey) && parentKey !== node.key) {
          byDisplayKey.get(parentKey).children.push(node);
          linkedEdges += 1;
          return;
        }
        roots.push(node);
      });

      const toFlatNodes = () =>
        Array.from(byDisplayKey.values())
          .filter((node) => (node.cat.memberIds || []).length > 0)
          .sort((a, b) => sortByDescricao(a.cat, b.cat))
          .map((node) => ({ ...node, children: [] }));

      const pruneRelevantTree = (nodes) => {
        const parentKeys = new Set();
        const pruneNode = (node) => {
          const prunedChildren = node.children
            .map(pruneNode)
            .filter(Boolean);
          const hasAvailableIds = (node.cat.memberIds || []).length > 0;
          const keep = hasAvailableIds || prunedChildren.length > 0;
          if (!keep) return null;
          if (prunedChildren.length > 0) parentKeys.add(node.key);
          return {
            ...node,
            children: prunedChildren,
          };
        };

        const prunedRoots = nodes.map(pruneNode).filter(Boolean);
        return {
          prunedRoots,
          parentKeys,
        };
      };

      // Sem relação pai-filho válida, mantém lista flat
      if (hasParentConflict || linkedEdges === 0 || roots.length === 0) {
        const flatNodes = toFlatNodes();
        return {
          nodes: flatNodes,
          mode: "flat",
          parentDisplayKeysSet: new Set(),
        };
      }

      const sortTree = (nodes) => {
        nodes.sort((a, b) => sortByDescricao(a.cat, b.cat));
        nodes.forEach((n) => sortTree(n.children));
      };
      sortTree(roots);
      const visited = new Set();
      const walk = (node) => {
        if (visited.has(node.key)) return;
        visited.add(node.key);
        node.children.forEach(walk);
      };
      roots.forEach(walk);

      // Estrutura inconsistente (ciclo/orfandade indevida): mantém flat
      if (visited.size !== byDisplayKey.size) {
        const flatNodes = toFlatNodes();
        return {
          nodes: flatNodes,
          mode: "flat",
          parentDisplayKeysSet: new Set(),
        };
      }

      const { prunedRoots, parentKeys } = pruneRelevantTree(roots);

      return {
        nodes: prunedRoots,
        mode: "tree",
        parentDisplayKeysSet: parentKeys,
      };
    }, [cats, getDisplayKey, isAvailableForCurrentSub, normalizeCode, sub.id]);

    const isTreeMode = treeState.mode === "tree";
    const isNodeSelectable = useCallback(
      (node) => {
        if (!node) return false;
        const key = String(node.key || "");
        const hasAvailableIds = (node.cat.memberIds || []).length > 0;
        if (!hasAvailableIds) return false;
        return !treeState.parentDisplayKeysSet?.has(key);
      },
      [treeState.parentDisplayKeysSet],
    );

    const filteredTree = useMemo(() => {
      const normalizedSearch = String(searchTerm || "").trim().toLowerCase();
      if (!normalizedSearch) return treeState.nodes;

      const includesTerm = (node) =>
        String(node.cat.descricao || "").toLowerCase().includes(normalizedSearch);

      const filterNode = (node) => {
        const filteredChildren = node.children
          .map(filterNode)
          .filter(Boolean);
        if (includesTerm(node) || filteredChildren.length > 0) {
          return {
            ...node,
            children: filteredChildren,
          };
        }
        return null;
      };

      const result = treeState.nodes.map(filterNode).filter(Boolean);
      return result;
    }, [treeState.nodes, searchTerm]);

    const visibleSelectableIds = useMemo(() => {
      const ids = [];
      const walk = (node) => {
        if (isNodeSelectable(node)) {
          ids.push(...(node.cat.memberIds || []));
        }
        node.children.forEach(walk);
      };
      filteredTree.forEach(walk);
      return Array.from(new Set(ids));
    }, [filteredTree, isNodeSelectable]);

    const allSelectableLeafIds = useMemo(() => {
      const ids = [];
      const walk = (node) => {
        if (isNodeSelectable(node)) {
          ids.push(...(node.cat.memberIds || []));
        }
        node.children.forEach(walk);
      };
      treeState.nodes.forEach(walk);
      return new Set(ids);
    }, [isNodeSelectable, treeState.nodes]);

    // Garante regra leaf-only mesmo para seleção inicial carregada do vínculo atual
    useEffect(() => {
      setBulkSelectedCatIds((prev) => {
        const cleaned = prev.filter((id) => allSelectableLeafIds.has(id));
        if (cleaned.length === prev.length) return prev;
        return cleaned;
      });
    }, [allSelectableLeafIds, setBulkSelectedCatIds]);

    const getDescendantSelectableIds = useCallback(
      (node) => {
        const ids = [];
        const walk = (current) => {
          current.children.forEach((child) => {
            if (isNodeSelectable(child)) ids.push(...(child.cat.memberIds || []));
            walk(child);
          });
        };
        walk(node);
        return Array.from(new Set(ids));
      },
      [isNodeSelectable],
    );

    const toggleSingle = useCallback(
      (node) => {
        const ids = node?.cat?.memberIds || [];
        if (!ids.length || !isNodeSelectable(node)) return;
        setBulkSelectedCatIds((prev) => {
          const allSelected = ids.every((id) => prev.includes(id));
          if (allSelected) return prev.filter((id) => !ids.includes(id));
          return Array.from(new Set([...prev, ...ids]));
        });
      },
      [isNodeSelectable, setBulkSelectedCatIds],
    );

    const toggleGroup = useCallback(
      (ids) => {
        if (!ids.length) return;
        setBulkSelectedCatIds((prev) => {
          const allSelected = ids.every((id) => prev.includes(id));
          if (allSelected) return prev.filter((id) => !ids.includes(id));
          return Array.from(new Set([...prev, ...ids]));
        });
      },
      [setBulkSelectedCatIds],
    );

    const handleSelectAll = useCallback(() => {
      setBulkSelectedCatIds((prev) =>
        Array.from(new Set([...prev, ...visibleSelectableIds])),
      );
    }, [setBulkSelectedCatIds, visibleSelectableIds]);

    const handleDeselectAll = useCallback(() => {
      if (searchTerm) {
        setBulkSelectedCatIds((prev) =>
          prev.filter((id) => !visibleSelectableIds.includes(id)),
        );
        return;
      }
      setBulkSelectedCatIds([]);
    }, [searchTerm, setBulkSelectedCatIds, visibleSelectableIds]);

    const renderTreeNode = (node, level = 0) => {
      const isGroupNode = isTreeMode && node.children.length > 0;
      const isSelectable = isNodeSelectable(node);
      const selfIds = node.cat.memberIds || [];
      const selfSelectedCount = selfIds.filter((id) => bulkSelectedCatIds.includes(id)).length;
      const selfChecked = selfIds.length > 0 && selfSelectedCount === selfIds.length;
      const selfIndeterminate = selfSelectedCount > 0 && !selfChecked;
      const groupIds = isGroupNode ? getDescendantSelectableIds(node) : [];
      const groupSelectedCount = groupIds.filter((id) =>
        bulkSelectedCatIds.includes(id),
      ).length;
      const groupChecked = groupIds.length > 0 && groupSelectedCount === groupIds.length;
      const groupIndeterminate = groupSelectedCount > 0 && !groupChecked;
      const checked = isGroupNode ? groupChecked : selfChecked;
      const indeterminate = isGroupNode ? groupIndeterminate : selfIndeterminate;

      const onToggle = () => {
        if (isGroupNode) {
          toggleGroup(groupIds);
          return;
        }
        toggleSingle(node);
      };

      return (
        <div key={node.key}>
          <div
            style={{
              padding: "8px 12px",
              cursor:
                isGroupNode && groupIds.length === 0
                  ? "default"
                  : isSelectable || isGroupNode
                    ? "pointer"
                    : "default",
              borderBottom: `1px solid ${theme.borderSecondary}`,
              backgroundColor: checked ? theme.infoBackground : "transparent",
              opacity: isGroupNode && groupIds.length === 0 ? 0.7 : 1,
            }}
            onClick={onToggle}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 12,
                paddingLeft: `${level * 18}px`,
              }}
            >
              <Checkbox
                checked={checked}
                indeterminate={indeterminate}
                disabled={isGroupNode ? groupIds.length === 0 : !isSelectable}
              />
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Text
                  style={{
                    fontSize: 13,
                    color: theme.textPrimary,
                    fontWeight: isGroupNode ? 600 : 400,
                  }}
                >
                  {node.cat.descricao}
                </Text>
              </div>
              {isGroupNode && <Tag style={{ marginLeft: "auto" }}>Grupo</Tag>}
              {node.cat.linkedToCurrentSub && (
                <Tag color="blue" style={{ marginLeft: "auto" }}>
                  Vinculado
                </Tag>
              )}
            </div>
          </div>
          {node.children.map((child) => renderTreeNode(child, level + 1))}
        </div>
      );
    };

    return (
      <div style={{ display: "flex", flexDirection: "column", height: "60vh" }}>
        <div style={{ marginBottom: 16, display: "flex", gap: 8 }}>
          <Input
            placeholder="Buscar categorias..."
            prefix={<SearchOutlined style={{ color: theme.textSecondary }} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            style={{
              flex: 1,
              backgroundColor: theme.inputBackground,
              color: theme.textPrimary,
              borderColor: theme.border,
            }}
          />
          <Button onClick={handleSelectAll}>Marcar Todos</Button>
          <Button onClick={handleDeselectAll}>Desmarcar Todos</Button>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            border: `1px solid ${theme.borderSecondary}`,
            borderRadius: 8,
            padding: 0,
            backgroundColor: theme.backgroundSecondary,
          }}
        >
          <div>{filteredTree.map((node) => renderTreeNode(node, 0))}</div>
          {filteredTree.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", color: theme.textSecondary }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Nenhuma categoria encontrada"
              />
            </div>
          )}
        </div>

        <div style={{ marginTop: 8, textAlign: "right" }}>
          <Text type="secondary" style={{ color: theme.textSecondary }}>
            {bulkSelectedCatIds.length} categorias selecionadas
          </Text>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.sub?.id === nextProps.sub?.id &&
    prevProps.cats === nextProps.cats &&
    prevProps.theme === nextProps.theme &&
    prevProps.bulkSelectedCatIds === nextProps.bulkSelectedCatIds,
);

const isTotalizador = (n) => !!(n?.isTotalizador ?? n?.IsTotalizador);
const isSubConta = (n) => !!(n?.isSubConta ?? n?.IsSubConta);
const isConta = (n) => !!(n?.isConta ?? n?.IsConta);
const isNodeDeleted = (n) => !!(n?.isDeleted ?? n?.IsDeleted);
const isNodeActive = (n) => (n?.isActive ?? n?.IsActive) !== false;
const getNodeId = (n) => n?.id ?? n?.Id ?? null;
const getNodeCodDre = (n) => n?.codDRE ?? n?.CodDRE ?? "";
const getNodeDescricao = (n) => n?.descricao ?? n?.Descricao ?? "";
const isTotalizadorAV = (n) => !!(n?.totalizadorAV ?? n?.TotalizadorAV);
const canUseAsAVBase = (n) => !isSubConta(n) && (isTotalizador(n) || isConta(n));
const AV_BASE_DEFAULT_VALUE = "__default_av_base__";

function pad2(i) {
  return String(i).padStart(2, "0");
}
function nextChildCode(parentCode, index1) {
  return `${parentCode}.${pad2(index1)}`;
}
function clone(obj) {
  return JSON.parse(JSON.stringify(obj));
}
function parseTopCode(c) {
  const n = parseInt(String(c || "0"), 10);
  return isNaN(n) ? 0 : n;
}

function buildIndexes(nodes) {
  const alive = nodes.filter((n) => !n.isDeleted);

  const totals = alive
    .filter(isTotalizador)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const totalsByCode = new Map(totals.map((t) => [String(t.codDRE), t]));


  const contasByTotal = new Map();
  for (const c of alive.filter(isConta)) {
    let total = totalsByCode.get(String(c.codDRESup)) || null;
    if (!total) {
      const inferred = String(c.codDRE).split(".")[0];
      if (inferred) total = totalsByCode.get(inferred) || null;
    }
    const key = total ? total.id : "__ROOT__";
    if (!contasByTotal.has(key)) contasByTotal.set(key, []);
    contasByTotal.get(key).push(c);
  }
  for (const arr of contasByTotal.values())
    arr.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const subsByConta = new Map();
  const contasByCode = new Map(
    alive.filter(isConta).map((c) => [String(c.codDRE), c]),
  );
  for (const s of alive.filter(isSubConta)) {
    let key = String(s.codDRESup || "__NONE__");
    if (!contasByCode.has(key)) {
      const inferred = String(s.codDRE).split(".").slice(0, 2).join(".");
      if (inferred) key = inferred;
    }
    if (!subsByConta.has(key)) subsByConta.set(key, []);
    subsByConta.get(key).push(s);
  }
  for (const arr of subsByConta.values())
    arr.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const totalsById = new Map(totals.map((t) => [t.id, t]));
  const contasById = new Map(alive.filter(isConta).map((c) => [c.id, c]));
  const subsById = new Map(alive.filter(isSubConta).map((s) => [s.id, s]));

  return {
    totals,
    contasByTotal,
    subsByConta,
    totalsByCode,
    totalsById,
    contasById,
    subsById,
  };
}

// Recalcula posições das Contas sob um Totalizador e ajusta códigos
function recalcContasUnderTotal(nodes, total) {
  const list = nodes
    .filter(
      (n) =>
        !n.isDeleted &&
        isConta(n) &&
        String(n.codDRESup) === String(total.codDRE),
    )
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));


  // Primeiro, coleta todas as subcontas que serão afetadas ANTES de alterar os códigos
  const affectedSubs = [];
  for (const s of nodes) {
    if (!s.isDeleted && isSubConta(s)) {
      const parentConta = list.find((c) => c.codDRE === s.codDRESup);
      if (parentConta) {
        affectedSubs.push({ sub: s, oldParentCode: s.codDRESup });
      }
    }
  }

  const mapOldToNew = new Map();
  list.forEach((c, i) => {
    const oldCode = c.codDRE;
    const newCode = nextChildCode(total.codDRE, i + 1);
    mapOldToNew.set(oldCode, newCode);
  });

  // Atualiza as contas
  list.forEach((c, i) => {
    c.position = i + 1;
    c.codDRESup = total.codDRE;
    c.codDRE = mapOldToNew.get(c.codDRE) || c.codDRE;
    c.isDirty = true;
  });

  // Atualiza as subcontas usando o mapeamento coletado ANTES das mudanças
  for (const { sub, oldParentCode } of affectedSubs) {
    const maybeNew = mapOldToNew.get(oldParentCode);
    if (maybeNew) {
      sub.codDRESup = maybeNew;
      sub.isDirty = true;
    }
  }

  // Recalcula as subcontas de cada conta
  for (const c of list) recalcSubsUnderConta(nodes, c);
}

// Recalcula posições das SubContas sob uma Conta e ajusta códigos
function recalcSubsUnderConta(nodes, conta) {
  const list = nodes
    .filter(
      (n) =>
        !n.isDeleted &&
        isSubConta(n) &&
        String(n.codDRESup) === String(conta.codDRE),
    )
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));


  // Só recalcula se há subcontas para recalcular
  if (list.length > 0) {
    list.forEach((s, i) => {
      s.position = i + 1;
      s.codDRESup = conta.codDRE;
      s.codDRE = nextChildCode(conta.codDRE, i + 1);
      s.isDirty = true;
    });
  }
}

function moveContaBetweenTotals(all, contaId, toTotal) {
  const n = all.find((x) => x.id === contaId);
  if (!n || !isConta(n)) return;
  n.codDRESup = String(toTotal.codDRE);
  n.isDirty = true;
  const siblings = all.filter(
    (x) => isConta(x) && x.codDRESup === toTotal.codDRE,
  );
  n.position = siblings.length; // ajustado depois por recalc
}

function moveSubBetweenContas(all, subId, toConta) {
  const n = all.find((x) => x.id === subId);
  if (!n || !isSubConta(n)) return;
  n.codDRESup = toConta.codDRE;
  n.isDirty = true;
  const siblings = all.filter(
    (x) => isSubConta(x) && x.codDRESup === toConta.codDRE,
  );
  n.position = siblings.length; // ajustado depois por recalc
}

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  return `id_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

// ----------------------- dnd helpers -----------------------
const CONTAINER_TOTALS = "totals";
const containerContasId = (totalId) => `contas:${totalId}`;
const containerSubsId = (contaCode) => `subs:${contaCode}`;
const CATS_UNLINKED = "cats_unlinked";
const containerCatsSubId = (subId) => `catsub:${subId}`;

function isContainerId(id) {
  return (
    id === CONTAINER_TOTALS ||
    String(id).startsWith("contas:") ||
    String(id).startsWith("subs:") ||
    id === CATS_UNLINKED ||
    String(id).startsWith("catsub:")
  );
}

// remapSubParent removido (não utilizado)

function reindexTotalsCascade(all) {
  const totals = all
    .filter((n) => !n.isDeleted && isTotalizador(n))
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));


  const oldCodes = totals.map((t) => String(t.codDRE));
  const newCodes = totals.map((_t, i) => String(i + 1));
  const mapOldToNew = new Map(oldCodes.map((oc, i) => [oc, newCodes[i]]));


  totals.forEach((t, i) => {
    t.position = i + 1;
    t.codDRE = newCodes[i];
    t.isDirty = true;
  });

  // Remapeia Contas: apenas o prefixo (primeiro segmento) e mantém o restante
  const contaCodeMap = new Map();

  for (const n of all) {
    if (!n.isDeleted && isConta(n)) {
      const oldSup = String(n.codDRESup);
      const newSup = mapOldToNew.get(oldSup);
      if (newSup) {
        const parts = String(n.codDRE).split(".");
        const newCode =
          parts.length > 1
            ? [String(newSup), ...parts.slice(1)].join(".")
            : String(newSup);
        contaCodeMap.set(String(n.codDRE), newCode);
        n.codDRESup = String(newSup);
        n.codDRE = newCode;
        n.isDirty = true;
      }
    }
  }

  // Remapeia SubContas com base no novo código das Contas, preservando segmentos finais
  for (const s of all) {
    if (!s.isDeleted && isSubConta(s)) {
      const parts = String(s.codDRE).split(".");
      if (parts.length >= 3) {
        const oldContaCode = [parts[0], parts[1]].join(".");
        const newContaCode = contaCodeMap.get(oldContaCode);
        if (newContaCode) {
          const cp = newContaCode.split(".");
          const newCode = [cp[0], cp[1], ...parts.slice(2)].join(".");
          s.codDRESup = [cp[0], cp[1]].join(".");
          s.codDRE = newCode;
          s.isDirty = true;
        }
      } else {
        const maybe = contaCodeMap.get(String(s.codDRESup));
        if (maybe) {
          s.codDRESup = String(maybe);
          s.isDirty = true;
        }
      }
    }
  }

}

/**Limpa categorias de subcontas apagadas */
function clearCatsForSubIds(catsArr, subIds) {
  if (!subIds?.length) return catsArr;
  const set = new Set(subIds);
  return catsArr.map((c) =>
    set.has(c.syncDREId) ? { ...c, syncDREId: ZERO_GUID, isDirty: true } : c,
  );
}

function isInteractive(el) {
  return el?.closest?.(
    'input, textarea, select, button, [contenteditable="true"], .ant-input, .ant-select, .ant-select-dropdown, .ant-picker, .ant-picker-panel, .ant-switch',
  );
}

function collectDescendantsForTotal(nodes, total) {
  const contas = nodes.filter(
    (n) =>
      !n.isDeleted &&
      isConta(n) &&
      String(n.codDRESup) === String(total.codDRE),
  );
  const contaCodes = new Set(contas.map((c) => String(c.codDRE)));
  const subs = nodes.filter(
    (n) => !n.isDeleted && isSubConta(n) && contaCodes.has(String(n.codDRESup)),
  );
  return { contas, subs };
}

function collectSubsForConta(nodes, conta) {
  return nodes.filter(
    (n) =>
      !n.isDeleted &&
      isSubConta(n) &&
      String(n.codDRESup) === String(conta.codDRE),
  );
}

function repairParentPointers(nodes) {
  const aliveTotalsCodes = new Set(
    nodes.filter((n) => !n.isDeleted && isTotalizador(n)).map((n) => n.codDRE),
  );
  const aliveContasCodes = new Set(
    nodes.filter((n) => !n.isDeleted && isConta(n)).map((n) => n.codDRE),
  );

  for (const c of nodes) {
    if (!c.isDeleted && isConta(c)) {
      if (!aliveTotalsCodes.has(c.codDRESup)) {
        const expectedSup = String(c.codDRE).split(".")[0] || null;
        if (expectedSup) {
          c.codDRESup = expectedSup;
          c.isDirty = true;
        }
      }
    }
  }

  for (const s of nodes) {
    if (!s.isDeleted && isSubConta(s)) {
      if (!aliveContasCodes.has(s.codDRESup)) {
        const expectedSup = String(s.codDRE).split(".").slice(0, 2).join(".");
        if (expectedSup && aliveContasCodes.has(expectedSup)) {
          s.codDRESup = expectedSup;
          s.isDirty = true;
        }
      }
    }
  }
}

function canSoftDeleteNode(nodes, node) {
  if (!node.isCustom) return false;
  if (isSubConta(node)) return true;

  if (isConta(node)) {
    const subs = collectSubsForConta(nodes, node);
    return subs.every((s) => s.isCustom);
  }

  if (isTotalizador(node)) {
    const { contas, subs } = collectDescendantsForTotal(nodes, node);
    return contas.every((c) => c.isCustom) && subs.every((s) => s.isCustom);
  }

  return false;
}

function markDeletedCascade(draft, node) {
  const subIdsToClear = [];
  const mark = (n) => {
    n.isDeleted = true;
    n.isDirty = true;
  };

  if (isSubConta(node)) {
    mark(node);
    subIdsToClear.push(node.id);
    return subIdsToClear;
  }

  if (isConta(node)) {
    mark(node);
    const subs = collectSubsForConta(draft, node);
    subs.forEach((s) => {
      mark(s);
      subIdsToClear.push(s.id);
    });
    return subIdsToClear;
  }

  if (isTotalizador(node)) {
    mark(node);
    const { contas, subs } = collectDescendantsForTotal(draft, node);
    contas.forEach(mark);
    subs.forEach((s) => {
      mark(s);
      subIdsToClear.push(s.id);
    });
    return subIdsToClear;
  }

  return subIdsToClear;
}

// Mouse/Touch sensors que NÃO ativam drag em inputs, selects, etc
class SafeMouseSensor extends MouseSensor {
  static activators = [
    {
      eventName: "onMouseDown",
      handler: ({ nativeEvent }) => !isInteractive(nativeEvent.target),
    },
  ];
}

class SafeTouchSensor extends TouchSensor {
  static activators = [
    {
      eventName: "onTouchStart",
      handler: ({ nativeEvent }) => !isInteractive(nativeEvent.target),
    },
  ];
}

function setActiveCascade(draft, node, checked) {
  const current = draft.find((x) => x.id === node.id);
  if (!current || current.isDeleted) return;

  const mark = (n) => {
    n.isActive = checked;
    n.isDirty = true;
  };

  mark(current);

  // Totalizador → Contas → SubContas
  if (isTotalizador(current)) {
    const contas = draft.filter(
      (n) => !n.isDeleted && isConta(n) && n.codDRESup === current.codDRE,
    );
    for (const c of contas) {
      mark(c);
      const subs = draft.filter(
        (n) => !n.isDeleted && isSubConta(n) && n.codDRESup === c.codDRE,
      );
      for (const s of subs) mark(s);
    }
  }
  // Conta → SubContas
  else if (isConta(current)) {
    const subs = draft.filter(
      (n) => !n.isDeleted && isSubConta(n) && n.codDRESup === current.codDRE,
    );
    for (const s of subs) mark(s);
  }
  // SubConta: já foi marcada acima
}

export default function DRETreeManager({
  syncId,
  initialNodes,
  initialCategories,
  onDreChange,
  onSave,
  isSaving,
  indicatorsContent,
  kpiContent,
  financialGroupsContent,
  searchTerm,
  onSearchChange,
  isEmbedded,
  menu,
  submenu,
  onTabChange,
}) {
  const { theme } = useTheme();
  const [nodes, setNodes] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncInfo, setSyncInfo] = useState(null);
  const [toggleErpDreSaving, setToggleErpDreSaving] = useState(false);
  const [pendingBaseAvDreId, setPendingBaseAvDreId] = useState(null);
  const [baseAvDirty, setBaseAvDirty] = useState(false);
  const [dreAlertRefresh, setDreAlertRefresh] = useState(0);
  const [defaultFinancialConfig, setDefaultFinancialConfig] = useState(null);
  const defaultFinancialRef = useRef(null);

  // Regra DRE por pagamento (Omie). `savedTipoDrePag` = valor persistido; `tipoDrePag` = seleção atual.
  const [tipoDrePag, setTipoDrePag] = useState(TIPO_DRE_PAGAMENTO_DEFAULT);
  const [savedTipoDrePag, setSavedTipoDrePag] = useState(TIPO_DRE_PAGAMENTO_DEFAULT);

  const [collapsedTotals, setCollapsedTotals] = useState(() => new Set());
  const [collapsedContas, setCollapsedContas] = useState(() => new Set());
  const [collapsedSubContas, setCollapsedSubContas] = useState(() => new Set());
  const [areAllCollapsed, setAreAllCollapsed] = useState(false);

  // Bulk Add Modal State
  const [isBulkAddModalOpen, setIsBulkAddModalOpen] = useState(false);
  const [targetSubForBulkAdd, setTargetSubForBulkAdd] = useState(null);
  const [bulkSelectedCatIds, setBulkSelectedCatIds] = useState([]);

  // Tabs State
  const [activeTab, setActiveTab] = useState("Structure"); // "Structure" | "Indicators"
  const [activeIndicatorSubMenu, setActiveIndicatorSubMenu] =
    useState("financial"); // "financial" | ...
  const [localSearch, setLocalSearch] = useState("");

  // Sync with props
  useEffect(() => {
    if (menu) {
       const tabMap = {
           "structure": "Structure",
           "indicators": "Indicators"
       };
       const newTab = tabMap[menu] || "Structure";
       setActiveTab(newTab);
    }
  }, [menu]);

  useEffect(() => {
    if (submenu) {
        setActiveIndicatorSubMenu(submenu);
    }
  }, [submenu]);

  const handleTabChange = (val) => {
    setActiveTab(val);
    if (onTabChange) {
      onTabChange(val, activeIndicatorSubMenu);
    }
  };

  const handleSubMenuChange = (val) => {
    setActiveIndicatorSubMenu(val);
    if (onTabChange) {
      onTabChange(activeTab, val);
    }
  };

  const containerRef = useRef(null);
  
  const [containerWidth, setContainerWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 9999,
  );

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      setContainerWidth(rect.width || window.innerWidth);
    };

    update();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => update());
      ro.observe(el);
      return () => ro.disconnect();
    }

    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const narrowBreakpoint = isEmbedded ? 1024 : 768;
  const isNarrow = containerWidth <= narrowBreakpoint;

  const globalSearch = searchTerm !== undefined ? searchTerm : localSearch;
  const setGlobalSearch = onSearchChange || setLocalSearch;
  const isUnlinkedCat = useCallback(
    (cat) =>
      cat.syncDREId === null ||
      cat.syncDREId === undefined ||
      cat.syncDREId === ZERO_GUID,
    [],
  );

  const catAssociationRules = useMemo(() => {
    const parentCodes = new Set();
    const catCodeById = new Map();
    const parentCodeById = new Map();

    cats.forEach((cat) => {
      const id = String(cat.id);
      const code = normalizeCategoryCode(cat.codCategoria);
      const parentCode = normalizeCategoryCode(cat.codCategoriaSup);
      catCodeById.set(id, code);
      parentCodeById.set(id, parentCode);
      if (parentCode && parentCode !== ZERO_GUID) {
        parentCodes.add(parentCode);
      }
    });

    const associableById = new Map();
    cats.forEach((cat) => {
      const id = String(cat.id);
      const code = catCodeById.get(id);
      const parentCode = parentCodeById.get(id);
      const isRoot = parentCode === ZERO_GUID;
      const isParent = !!code && parentCodes.has(code);
      associableById.set(id, !!code && !isRoot && !isParent);
    });

    return {
      associableById,
      catCodeById,
      parentCodes,
      parentCodeById,
    };
  }, [cats]);

  const didInitRef = useRef(false);
  const lastSyncIdRef = useRef(null);
  const triedLoadDreRef = useRef(false);
  const syncInfoLoadedRef = useRef(false);
  const syncErpRef = useRef(null);

  const {
    totals,
    contasByTotal,
    subsByConta,
    totalsById,
    contasById,
    subsById,
  } = useMemo(() => buildIndexes(nodes), [nodes]);

  const currentErp = Number(syncInfo?.erp);
  const currentSyncType = Number(syncInfo?.type);
  const isContaAzulV2 = currentErp === 5 && currentSyncType === 1;
  const isOmie = currentErp === 0;
  const isNiboStandard = currentErp === 2 && currentSyncType === 1;
  const supportsErpDefaultDre = isContaAzulV2 || isOmie || isNiboStandard;
  const erpDefaultDreLabel = isContaAzulV2
    ? "Conta Azul"
    : isOmie
      ? "Omie"
      : isNiboStandard
        ? "Nibo"
        : "ERP";
  const useErpDreFlag =
    syncInfo?.useErpDreConfig ?? syncInfo?.UseErpDreConfig ?? false;
  const dreConfiguratorReadOnly = supportsErpDefaultDre && !!useErpDreFlag;

  // Regra DRE por pagamento só se aplica a sincronizações Omie.
  const tipoDrePagDirty = isOmie && tipoDrePag !== savedTipoDrePag;

  const handleTipoDrePagChange = useCallback((value) => {
    setTipoDrePag(normalizeTipoDrePagamento(value));
  }, []);

  const savedBaseAvDreId = useMemo(() => {
    const found = nodes.find((n) => isTotalizadorAV(n));
    return found ? getNodeId(found) : null;
  }, [nodes]);

  useEffect(() => {
    setBaseAvDirty(false);
    setPendingBaseAvDreId(null);
  }, [syncId]);

  useEffect(() => {
    if (baseAvDirty) return;
    setPendingBaseAvDreId(savedBaseAvDreId);
  }, [savedBaseAvDreId, baseAvDirty]);

  const avBaseOptions = useMemo(() => {
    return nodes
      .filter(
        (n) => !isNodeDeleted(n) && isNodeActive(n) && canUseAsAVBase(n),
      )
      .sort((a, b) =>
        String(getNodeCodDre(a)).localeCompare(String(getNodeCodDre(b)), "pt-BR", {
          numeric: true,
        }),
      )
      .map((n) => ({
        value: getNodeId(n),
        label: `${getNodeCodDre(n)} - ${getNodeDescricao(n)} (${isTotalizador(n) ? "Totalizador" : "Conta"})`,
      }));
  }, [nodes]);

  const avBaseSelectOptions = useMemo(
    () => [
      {
        value: AV_BASE_DEFAULT_VALUE,
        label: "Usar padrão: primeiro totalizador do DRE",
      },
      ...avBaseOptions,
    ],
    [avBaseOptions],
  );

  const handleBaseAvSelectionChange = useCallback(
    (value) => {
      const nextId =
        value === AV_BASE_DEFAULT_VALUE || value === null || value === undefined
          ? null
          : value;
      setPendingBaseAvDreId(nextId);
      setBaseAvDirty(nextId !== savedBaseAvDreId);
    },
    [savedBaseAvDreId],
  );

  const applyTotalizadorAVToNodes = useCallback((targetNodeId) => {
    setNodes((prev) =>
      prev.map((node) => {
        const nodeId = getNodeId(node);
        const nextValue = targetNodeId && nodeId === targetNodeId ? true : null;
        const currentValue = isTotalizadorAV(node) ? true : null;
        if (currentValue === nextValue) return node;
        return {
          ...node,
          totalizadorAV: nextValue,
          TotalizadorAV: undefined,
        };
      }),
    );
  }, []);

  const handleSaveAllChanges = useCallback(async () => {
    if (!onSave) return;
    const saveSucceeded = await onSave({
      nodes,
      cats,
      activeTab,
      activeIndicatorSubMenu,
      dreConfiguratorReadOnly,
      // Regra DRE por pagamento (Omie): enviada junto do fluxo normal de saveDreConfig.
      tipoDrePag: isOmie ? tipoDrePag : undefined,
      tipoDrePagDirty,
      baseAvDreId: pendingBaseAvDreId,
      baseAvDirty,
      defaultFinancialItems: defaultFinancialConfig?.saveItems || null,
      defaultFinancialDirty: !!defaultFinancialConfig?.isDirty,
    });

    if (!saveSucceeded) return;

    setDreAlertRefresh((t) => t + 1);

    if (baseAvDirty) {
      applyTotalizadorAVToNodes(pendingBaseAvDreId);
      setBaseAvDirty(false);
    }

    if (tipoDrePagDirty) {
      setSavedTipoDrePag(tipoDrePag);
    }

    if (defaultFinancialRef.current?.markAsSaved) {
      defaultFinancialRef.current.markAsSaved();
    }
  }, [
    activeIndicatorSubMenu,
    activeTab,
    applyTotalizadorAVToNodes,
    baseAvDirty,
    cats,
    defaultFinancialConfig,
    dreConfiguratorReadOnly,
    isOmie,
    nodes,
    onSave,
    pendingBaseAvDreId,
    tipoDrePag,
    tipoDrePagDirty,
  ]);

  const handleUseErpDreConfigChange = useCallback(
    async (checked) => {
      if (!syncId) return;
      // Ao habilitar, precisa confirmar pois sobrescreve DRE + Indicadores.
      if (checked === true) {
        Modal.confirm({
          title: "Confirmar sobrescrita",
          content: isContaAzulV2
            ? "Suas configurações de Indicadores de DRE serão apagadas e substituídas pelo padrão Conta Azul. \n\n" +
              "Você precisará reconfigurá-los manualmente após a atualização. Esta operação pode levar alguns minutos." +
              "Deseja continuar?"
            : "Ao habilitar esta opção, o DRE passará a seguir o padrão do ERP selecionado e ficará bloqueado para edição manual enquanto estiver ativo. Deseja continuar?",
          okText: "Confirmar e continuar",
          cancelText: "Cancelar",
          cancelButtonProps: { danger: true, type: "primary" },
          okButtonProps: { type: "default" },
          onOk: async () => {
            setToggleErpDreSaving(true);
            try {
              await SynchronizationService.enableErpDreConfig(syncId);
              setSyncInfo((prev) =>
                prev ? { ...prev, useErpDreConfig: true } : prev,
              );
              // Limpa UI local imediatamente; a repopulação vem do integrador (async).
              setNodes([]);
              setCats([]);
              Toast.success(
                "Configurações apagadas. Atualização do DRE solicitada.",
              );
            } catch (e) {
              console.error(e);
              Toast.error("Não foi possível habilitar esta opção.");
            } finally {
              setToggleErpDreSaving(false);
            }
          },
        });
        return;
      }

      // Ao desabilitar, mantém o comportamento atual (somente toggle).
      setToggleErpDreSaving(true);
      try {
        await SynchronizationService.setSyncUseErpDreConfig(syncId, false);
        setSyncInfo((prev) => (prev ? { ...prev, useErpDreConfig: false } : prev));
        Toast.success("Configurador do DRE liberado para edição.");
      } catch (e) {
        console.error(e);
        Toast.error("Não foi possível atualizar esta opção.");
      } finally {
        setToggleErpDreSaving(false);
      }
    },
    [isContaAzulV2, syncId],
  );

  // dnd sensors
  const sensors = useSensors(
    useSensor(SafeMouseSensor, {
      activationConstraint: { distance: 5 },
      disabled: dreConfiguratorReadOnly,
    }),
    useSensor(SafeTouchSensor, {
      activationConstraint: { delay: 150, tolerance: 8 },
      disabled: dreConfiguratorReadOnly,
    }),
  );

  // Carregar informações da sincronização
  useEffect(() => {
    const loadSyncInfo = async () => {
      if (!syncId) return;

      try {
        const syncData =
          await SynchronizationService.getSynchronizationById(syncId);
        const syncInfoData = syncData?.response || syncData;
        setSyncInfo(syncInfoData);
        syncErpRef.current = syncInfoData?.erp;
        syncInfoLoadedRef.current = true;
      } catch (error) {
        console.error("Erro ao carregar informações da sincronização:", error);
      }
    };

    if (lastSyncIdRef.current !== syncId) {
      lastSyncIdRef.current = syncId;
      didInitRef.current = false;
      triedLoadDreRef.current = false;
      syncInfoLoadedRef.current = false;
      syncErpRef.current = null;
      loadSyncInfo();
    }
  }, [syncId]);

  // Carregar dados iniciais se disponíveis
  useEffect(() => {
    if (!syncId || didInitRef.current || triedLoadDreRef.current) return;

    if (Array.isArray(initialNodes) || Array.isArray(initialCategories)) {
      setNodes(initialNodes || []);
      setCats(initialCategories || []);
      didInitRef.current = true;
      triedLoadDreRef.current = true;
      return;
    }

    // Só tenta carregar do servidor quando syncInfo foi carregado (uma vez)
    // if (!syncInfoLoadedRef.current) return;

    triedLoadDreRef.current = true;
    const loadDreConfig = async () => {
      try {
        setLoading(true);
        const dreConf =
          await SynchronizationService.getSynchronizationDreConfig(syncId);
        const dreNodes = dreConf.syncDre || [];
        const dreCats = dreConf.syncDreCat || [];

        // Regra DRE por pagamento: usa o valor retornado; fallback para o padrão (Tipo 2).
        const tipo = normalizeTipoDrePagamento(
          dreConf.tipoDrePag ?? dreConf.TipoDrePag,
        );
        setTipoDrePag(tipo);
        setSavedTipoDrePag(tipo);

        // Usa o ERP do ref para evitar dependência de syncInfo
        const currentErp = syncErpRef.current;

        // Para Excel, só atualiza se houver dados (senão deixa vazio para o botão)
        // Para outros ERPs, sempre atualiza
        if (currentErp === 3) {
          if (dreNodes.length > 0 || dreCats.length > 0) {
            setNodes(dreNodes);
            setCats(dreCats);
            didInitRef.current = true;
          }
        } else {
          setNodes(dreNodes);
          setCats(dreCats);
          didInitRef.current = true;
        }
      } catch (error) {
        // Não mostra erro se for Excel sem config (usuário vai criar)
        const currentErp = syncErpRef.current;
        if (currentErp !== 3) {
          Toast.error("Erro ao carregar configurações do DRE");
        }
        console.error("Erro ao carregar configurações do DRE:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDreConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncId, initialNodes, initialCategories]);

  // Notificar o pai quando nodes/cats mudarem
  useEffect(() => {
    if (typeof onDreChange === "function") {
      onDreChange({ nodes, cats });
    }
  }, [nodes, cats]);

  // --------------- Helpers de containers/ids ---------------
  const getItemType = (id) => {
    if (String(id).startsWith("cat:")) return "cat";
    if (String(id).startsWith("catgroup:")) return "cat";
    if (totalsById.has(id)) return "total";
    if (contasById.has(id)) return "conta";
    if (subsById.has(id)) return "sub";
    if (isContainerId(id)) return "container";
    return null;
  };

  const getContaParentTotal = (conta) => {
    // total cuja codDRE === conta.codDRESup
    return totals.find((t) => String(t.codDRE) === String(conta.codDRESup));
  };

  const getSubParentConta = (sub) => {
    return nodes.find(
      (n) => isConta(n) && String(n.codDRE) === String(sub.codDRESup),
    );
  };

  // Verificar se já existe uma conta com isCusto = true
  const hasExistingCostAccount = () => {
    return nodes.some((n) => !n.isDeleted && isConta(n) && n.isCusto === true);
  };

  // Criar configuração inicial de DRE para Excel
  const handleCreateInitialDreConfig = () => {
    if (dreConfiguratorReadOnly) return;
    const hasExistingConfig = nodes && nodes.length > 0;

    const applyTemplate = async () => {
      if (!syncId) {
        Toast.error("Sincronização não identificada para reset do DRE");
        return;
      }

      try {
        setLoading(true);
        await SynchronizationService.setExcelDreConfig(syncId);
        const dreConf =
          await SynchronizationService.getSynchronizationDreConfig(syncId);
        const dreNodes = dreConf?.syncDre || [];
        const dreCats = dreConf?.syncDreCat || [];
        setNodes(dreNodes);
        setCats(dreCats);
        didInitRef.current = true;
        Toast.success("Configuração inicial de DRE aplicada com sucesso!");
      } catch (error) {
        console.error("Erro ao resetar configuração inicial de DRE:", error);
        Toast.error("Erro ao aplicar configuração inicial de DRE");
      } finally {
        setLoading(false);
      }
    };

    if (hasExistingConfig) {
      Modal.confirm({
        title: "Resetar Configuração de DRE",
        content:
          "Esta ação irá substituir a configuração atual pelo template padrão (Receita > Deduções > Custos > Despesas). Todas as personalizações atuais serão perdidas. Deseja continuar?",
        okText: "Sim, resetar",
        okType: "danger",
        cancelText: "Cancelar",
        onOk: applyTemplate,
      });
    } else {
      applyTemplate();
    }
  };

  // ————————————————————————————————————————————————————————————————————————————————
  // Ações (Adicionar/Remover/Editar) — inalteradas
  // ————————————————————————————————————————————————————————————————————————————————
  function addTotalizador() {
    if (dreConfiguratorReadOnly) return;
    const draft = clone(nodes);
    const totals = draft
      .filter(isTotalizador)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const nextNumber =
      (totals.length
        ? Math.max(...totals.map((t) => parseTopCode(t.codDRE)))
        : 0) + 1;
    const newIndex = totals.length + 1;
    const novo = {
      id: uuid(),
      codDRE: String(nextNumber),
      codDRESup: null,
      descricao: "(Novo Totalizador)",
      descricaoOriginal: "",
      syncId: syncId,
      isTotalizador: true,
      isSubConta: false,
      isConta: false,
      isCustom: true,
      position: newIndex,
      code: String(nextNumber),
      syncDRECats: [],
      isActive: true,
      isDirty: true,
      isDeleted: false,
      isNew: true,
    };
    draft.push(novo);
    setNodes(draft);
  }

  function addConta(total) {
    if (dreConfiguratorReadOnly) return;
    const draft = clone(nodes);
    const siblings = draft
      .filter((n) => isConta(n) && n.codDRESup === total.codDRE)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const newIndex = siblings.length + 1;
    const novo = {
      id: uuid(),
      codDRE: nextChildCode(total.codDRE, newIndex),
      codDRESup: total.codDRE,
      descricao: "(Nova Conta)",
      descricaoOriginal: "",
      syncId: syncId,
      isTotalizador: false,
      isSubConta: false,
      isConta: true,
      isCustom: true,
      isCusto: false,
      position: newIndex,
      code: nextChildCode(total.codDRE, newIndex),
      syncDRECats: [],
      isActive: true,
      isDirty: true,
      isDeleted: false,
      isNew: true,
    };
    draft.push(novo);
    recalcContasUnderTotal(draft, total);
    setNodes(draft);
  }

  function addSubConta(conta) {
    if (dreConfiguratorReadOnly) return;
    const draft = clone(nodes);
    const siblings = draft
      .filter((n) => isSubConta(n) && n.codDRESup === conta.codDRE)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const newIndex = siblings.length + 1;
    const novo = {
      id: uuid(),
      codDRE: nextChildCode(conta.codDRE, newIndex),
      codDRESup: conta.codDRE,
      descricao: "(Nova SubConta)",
      descricaoOriginal: "",
      syncId: syncId,
      isTotalizador: false,
      isSubConta: true,
      isConta: false,
      isCustom: true,
      position: newIndex,
      code: nextChildCode(conta.codDRE, newIndex),
      syncDRECats: [],
      isActive: true,
      isDirty: true,
      isDeleted: false,
      isNew: true,
    };
    draft.push(novo);
    recalcSubsUnderConta(draft, conta);
    setNodes(draft);
  }

  function addCustoConta(total) {
    if (dreConfiguratorReadOnly) return;
    const draft = clone(nodes);
    const siblings = draft
      .filter((n) => isConta(n) && n.codDRESup === total.codDRE)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const newIndex = siblings.length + 1;

    const novaConta = {
      id: uuid(),
      codDRE: nextChildCode(total.codDRE, newIndex),
      codDRESup: total.codDRE,
      descricao: "Custo Médio de Vendas",
      descricaoOriginal: "",
      syncId: syncId,
      isTotalizador: false,
      isSubConta: false,
      isConta: true,
      isCustom: true,
      isCusto: true,
      position: newIndex,
      code: nextChildCode(total.codDRE, newIndex),
      syncDRECats: [],
      isActive: true,
      isDirty: true,
      isDeleted: false,
      isNew: true,
    };

    draft.push(novaConta);
    recalcContasUnderTotal(draft, total);

    // Criar subconta com o mesmo nome
    const novaSubConta = {
      id: uuid(),
      codDRE: nextChildCode(novaConta.codDRE, 1),
      codDRESup: novaConta.codDRE,
      descricao: "Custo Médio de Vendas",
      descricaoOriginal: "",
      syncId: syncId,
      isTotalizador: false,
      isSubConta: true,
      isConta: false,
      isCustom: true,
      position: 1,
      code: nextChildCode(novaConta.codDRE, 1),
      syncDRECats: [],
      isActive: true,
      isDirty: true,
      isDeleted: false,
      isNew: true,
    };

    draft.push(novaSubConta);

    // Vincular a categoria 99 caso ela esteja na lista de categorias
    const updatedCats = cats.map((cat) => {
      if (cat.codCategoria === "9999999") {
        return { ...cat, syncDREId: novaSubConta.id, isDirty: true };
      }
      return cat;
    });

    setCats(updatedCats);
    setNodes(draft);
  }

  function removeNode(node) {
    if (dreConfiguratorReadOnly) return;
    const draft = clone(nodes);
    const current = draft.find((n) => n.id === node.id);
    if (!current) return;

    // valida: o nó e TODOS os filhos precisam ser isCustom
    if (!canSoftDeleteNode(draft, current)) {
      message.error(
        "Não é possível excluir: existe filho (ou o próprio nó) que não é custom.",
      );
      return;
    }

    // marca deletado em cascata e coleta subcontas para limpar vínculos
    const subIdsToClear = markDeletedCascade(draft, current);

    // recálculo/renumeração para os sobreviventes
    if (isTotalizador(current)) {
      reindexTotalsCascade(draft);
    } else if (isConta(current)) {
      const total = draft.find(
        (t) =>
          !t.isDeleted && isTotalizador(t) && t.codDRE === current.codDRESup,
      );
      if (total) recalcContasUnderTotal(draft, total);
    } else if (isSubConta(current)) {
      const conta = draft.find(
        (c) => !c.isDeleted && isConta(c) && c.codDRE === current.codDRESup,
      );
      if (conta) recalcSubsUnderConta(draft, conta);
    }

    // aplica nodes e limpa vínculos das subcontas deletadas (batched)
    clearCatsForSubIdsAndSet(draft, subIdsToClear);
  }

  function toggleActive(node, checked) {
    if (dreConfiguratorReadOnly) return;
    const draft = clone(nodes);
    setActiveCascade(draft, node, checked);
    setNodes(draft);
  }

  const saveDescricao = useCallback(
    (nodeId, value) => {
      if (dreConfiguratorReadOnly) return;
      setNodes((prev) => {
        const i = prev.findIndex((x) => x.id === nodeId);
        if (i === -1) return prev;
        const next = [...prev];
        next[i] = { ...prev[i], descricao: value, isDirty: true };
        return next;
      });
    },
    [dreConfiguratorReadOnly],
  );

  // Colapso
  const isTotalCollapsed = (t) => collapsedTotals.has(t.id);
  const isContaCollapsed = (c) => collapsedContas.has(c.id);
  const toggleTotal = (t) =>
    setCollapsedTotals((prev) => {
      const next = new Set(prev);
      next.has(t.id) ? next.delete(t.id) : next.add(t.id);
      return next;
    });
  const toggleConta = (c) =>
    setCollapsedContas((prev) => {
      const next = new Set(prev);
      next.has(c.id) ? next.delete(c.id) : next.add(c.id);
      return next;
    });

  const isSubContaCollapsed = (s) => collapsedSubContas.has(s.id);
  const toggleSubConta = (s) =>
    setCollapsedSubContas((prev) => {
      const next = new Set(prev);
      next.has(s.id) ? next.delete(s.id) : next.add(s.id);
      return next;
    });

  const handleToggleExpandAll = () => {
    if (areAllCollapsed) {
      // Expandir tudo (limpar sets de colapso)
      setCollapsedTotals(new Set());
      setCollapsedContas(new Set());
      setCollapsedSubContas(new Set());
      setAreAllCollapsed(false);
    } else {
      // Colapsar tudo (adicionar todos IDs aos sets)
      const allTotals = new Set(nodes.filter(isTotalizador).map((n) => n.id));
      const allContas = new Set(nodes.filter(isConta).map((n) => n.id));
      const allSubs = new Set(nodes.filter(isSubConta).map((n) => n.id));

      setCollapsedTotals(allTotals);
      setCollapsedContas(allContas);
      setCollapsedSubContas(allSubs);
      setAreAllCollapsed(true);
    }
  };

  // Bulk Add Modal Actions
  const handleBulkAddOpen = (subNode) => {
    if (dreConfiguratorReadOnly) return;
    setTargetSubForBulkAdd(subNode);
    // Initialize with categories already linked to this subaccount
    const alreadyLinked = cats
      .filter((c) => c.syncDREId === subNode.id)
      .map((c) => c.id);
    setBulkSelectedCatIds(alreadyLinked);
    setIsBulkAddModalOpen(true);
  };

  const handleBulkAddClose = () => {
    setIsBulkAddModalOpen(false);
    setTargetSubForBulkAdd(null);
    setBulkSelectedCatIds([]);
  };

  const handleBulkAddSave = () => {
    if (dreConfiguratorReadOnly) return;
    if (!targetSubForBulkAdd) return;

    // Categories that are newly selected (and were not linked to this sub before)
    // Categories that were unselected (and were linked to this sub before)

    // We will set syncDREId = targetSub.id for all selected IDs
    // AND set syncDREId = null (or ZERO_GUID) for categories that were linked to this sub but are NOT in the selected list anymore.
    // Note: We only touch categories that are currently unlinked OR linked to THIS sub.
    // We should probably NOT steal categories from OTHER subs implicitly unless explicitly handled,
    // but the "Available Categories" list implies unlinked ones.

    // However, if the modal shows ALL available categories (unlinked + current), then unchecking means unlink.

    setCats((prevCats) => {
      return prevCats.map((c) => {
        // If category is in the selected list -> Link to this sub
        if (bulkSelectedCatIds.includes(c.id)) {
          // Only update if it's not already linked to avoid unnecessary dirty flags if possible,
          // but keeping it simple: ensure it points to target
          if (c.syncDREId !== targetSubForBulkAdd.id) {
            return { ...c, syncDREId: targetSubForBulkAdd.id, isDirty: true };
          }
          return c;
        }

        // If category was linked to this sub BUT is NOT in the selected list -> Unlink
        if (
          c.syncDREId === targetSubForBulkAdd.id &&
          !bulkSelectedCatIds.includes(c.id)
        ) {
          return { ...c, syncDREId: ZERO_GUID, isDirty: true };
        }

        return c;
      });
    });

    handleBulkAddClose();
  };

  // ----------------------- UI menores -----------------------

  function renderNodeHeader({
    node,
    tag,
    collapsible,
    collapsed,
    onToggle,
    childCount,
    onBulkAdd, // New prop
  }) {
    return (
      <Space
        align="center"
        style={{ width: "100%", justifyContent: "space-between" }}
      >
        <Space align="center">
          <HolderOutlined
            style={{
              cursor: dreConfiguratorReadOnly ? "default" : "grab",
              color: theme.textSecondary,
              fontSize: "16px",
              marginRight: 4,
            }}
          />
          {collapsible && (
            <Tooltip title={collapsed ? "Expandir" : "Colapsar"}>
              <Button
                onClick={onToggle}
                type="text"
                size="small"
                style={{ color: theme.textPrimary }}
                icon={
                  // Totalizador usa caret direita/cima, outros usam direita/baixo
                  tag.label === "Totalizador" ? (
                    collapsed ? (
                      <CaretRightOutlined />
                    ) : (
                      <CaretUpOutlined />
                    )
                  ) : collapsed ? (
                    <CaretRightOutlined />
                  ) : (
                    <CaretDownOutlined />
                  )
                }
              />
            </Tooltip>
          )}

          <div
            style={{
              justifyContent: "center",
              flexDirection: "column",
              alignContent: "center",
              minWidth: 0,
              maxWidth: "100%",
              display: "flex",
              gap: 4,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  fontSize: "10px",
                  color: theme.textSecondary,
                  textTransform: "uppercase",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                  border: `1px solid ${theme.borderSecondary}`,
                  padding: "1px 4px",
                  borderRadius: "4px",
                }}
              >
                {tag.label}
              </span>
              <Tooltip title="Clique para editar o título">
                <EditOutlined
                  style={{
                    color: theme.textSecondary,
                    fontSize: 12,
                  }}
                />
              </Tooltip>
            </div>
            <Input
              value={node.descricao ?? ""}
              onChange={(e) => saveDescricao(node.id, e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onPressEnter={(e) => {
                e.preventDefault();
                e.currentTarget.blur();
              }}
              disabled={dreConfiguratorReadOnly}
              style={{
                fontWeight: 600,
                width: node.isSubConta
                  ? "clamp(120px, 30vw, 280px)"
                  : "clamp(160px, 40vw, 480px)",
                fontSize: node.isSubConta ? 12 : 14,
                padding: node.isSubConta ? "0px" : undefined,
                height: node.isSubConta ? 20 : 28,
                border: "none",
                background: "transparent",
                boxShadow: "none",
                marginTop: -4,
                color: theme.textPrimary,
              }}
            />
            {!node.isSubConta &&
              node.descricaoOriginal &&
              node.descricaoOriginal !== node.descricao && (
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, marginLeft: 0, color: theme.textSecondary }}
                  >
                    {node.descricaoOriginal}
                  </Text>
                </div>
              )}
          </div>
        </Space>
        <Space>
          {/* Badge Count */}
          <div
            style={{
              backgroundColor: theme.backgroundTertiary,
              border: `1px solid ${theme.border}`,
              borderRadius: "12px",
              padding: "0 8px",
              height: "22px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              color: theme.textSecondary,
              minWidth: "24px",
            }}
          >
            {childCount || 0}
          </div>

          {node.isTotalizador && (
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <Tooltip title="Adicionar Conta">
                <Button
                  icon={<PlusOutlined />}
                  size="small"
                  onClick={() => addConta(node)}
                  disabled={collapsed || dreConfiguratorReadOnly}
                  style={{ color: theme.textPrimary, borderColor: theme.border }}
                />
              </Tooltip>
              {(syncInfo?.erp === 1 || syncInfo?.erp === 5) &&
                !hasExistingCostAccount() && (
                  <Tooltip title="Adicionar Custo de Venda">
                    <Button
                      icon={<PlusOutlined />}
                      size="small"
                      onClick={() => addCustoConta(node)}
                      disabled={collapsed || dreConfiguratorReadOnly}
                      style={{
                        backgroundColor: theme.infoBackground,
                        borderColor: theme.primary,
                        color: theme.primary,
                      }}
                    />
                  </Tooltip>
                )}
            </div>
          )}
          {node.isConta && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Tooltip title="Adicionar SubConta">
                <Button
                  icon={<PlusOutlined />}
                  size="small"
                  onClick={() => addSubConta(node)}
                  disabled={collapsed || dreConfiguratorReadOnly}
                  style={{ color: theme.textPrimary, borderColor: theme.border }}
                />
              </Tooltip>
            </div>
          )}
          {node.isSubConta && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Tooltip title="Selecionar Categorias em Lote">
                <Button
                  icon={<UnorderedListOutlined />}
                  size="small"
                  onClick={onBulkAdd}
                  disabled={collapsed || dreConfiguratorReadOnly}
                  style={{ color: theme.textPrimary, borderColor: theme.border }}
                />
              </Tooltip>
            </div>
          )}
          <Switch
            checked={!!node.isActive}
            onChange={(v) => toggleActive(node, v)}
            size="small"
            disabled={dreConfiguratorReadOnly}
          />
          {node.isCustom && (
            <Tooltip title="Remover">
              <Button
                icon={<DeleteOutlined />}
                onClick={() => removeNode(node)}
                danger
                size="small"
                disabled={dreConfiguratorReadOnly}
              />
            </Tooltip>
          )}
        </Space>
      </Space>
    );
  }
  // ----------------------- Renderizações -----------------------
  function renderSubContas(conta) {
    const subs = subsByConta.get(conta.codDRE) || [];
    const collapsed = isContaCollapsed(conta);
    const contId = containerSubsId(conta.codDRE);

    return (
      <div>
        {!(globalSearch ? false : collapsed) && (
          <DroppableList id={contId} style={{ display: "grid", gap: 6 }}>
            <SortableContext
              items={subs.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {subs.length === 0 && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Sem subcontas"
                />
              )}
              {subs.map((s) => {
                if (globalSearch && !visibleNodes.has(s.id)) return null;
                return (
                  <SortableCard
                    key={s.id}
                    id={s.id}
                    dragDisabled={dreConfiguratorReadOnly}
                    className="node-subconta"
                    bodyStyle={{ padding: 6 }}
                    style={{
                      backgroundColor: theme.cardBackground,
                      border: `1px solid ${theme.borderSecondary}`,
                      borderRadius: 10,
                    }}
                  >
                    {renderNodeHeader({
                      node: s,
                      tag: { label: "SubConta", color: "blue" },
                      childCount: cats.filter((c) => c.syncDREId === s.id).length,
                      collapsible: true,
                      collapsed: globalSearch ? false : isSubContaCollapsed(s),
                      onToggle: () => toggleSubConta(s),
                      onBulkAdd: () => handleBulkAddOpen(s),
                    })}
                    {!(globalSearch ? false : isSubContaCollapsed(s)) && (
                      <div style={{ marginTop: 8 }}>
                        {renderSubContaCats(s)}
                      </div>
                    )}
                  </SortableCard>
                );
              })}
            </SortableContext>
          </DroppableList>
        )}
      </div>
    );
  }

  function renderSubContaCats(sub) {
    const subCats = cats
      .filter((c) => c.syncDREId === sub.id)
      .filter(
        (c) =>
          !globalSearch ||
          c.descricao.toLowerCase().includes(globalSearch.toLowerCase()) ||
          String(c.codCategoria).includes(globalSearch.toLowerCase()),
      )
      .sort((a, b) =>
        String(a.codCategoria).localeCompare(String(b.codCategoria), "pt-BR"),
      );
    const contId = containerCatsSubId(sub.id);
    const items = subCats.map((c) => `cat:${c.id}`);
    return (
      <DroppableList
        id={contId}
        style={{
          display: "grid",
          gap: 10,
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        }}
      >
        <SortableContext items={items} strategy={rectSortingStrategy}>
          {subCats.length === 0 && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Sem categorias"
            />
          )}
          {subCats.map((c) => (
            <SortableCard
              key={`cat:${c.id}`}
              id={`cat:${c.id}`}
              dragDisabled={dreConfiguratorReadOnly}
              className="cat-chip"
              bodyStyle={{ padding: 6 }}
              style={{
                backgroundColor: theme.backgroundSecondary,
                border: `1px solid ${theme.borderSecondary}`,
                borderRadius: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <HolderOutlined style={{ fontSize: 12, color: theme.textSecondary }} />
                <span
                  style={{
                    flex: 1,
                    fontSize: 11,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: theme.textPrimary,
                  }}
                >
                  {c.descricao}
                </span>

                {c.natureza && (
                  <Tag
                    color={
                      String(c.natureza).toUpperCase() === "RECEITA"
                        ? "blue"
                        : "red"
                    }
                    style={{ fontSize: 10 }}
                  >
                    {c.natureza}
                  </Tag>
                )}
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  disabled={dreConfiguratorReadOnly}
                  onClick={() =>
                    setCats((prev) =>
                      prev.map((x) =>
                        String(x.id) === String(c.id)
                          ? { ...x, syncDREId: ZERO_GUID, isDirty: true }
                          : x,
                      ),
                    )
                  }
                />
              </div>
            </SortableCard>
          ))}
        </SortableContext>
      </DroppableList>
    );
  }

  function renderContas(total) {
    const contas = contasByTotal.get(total.id) || [];
    const collapsed = isTotalCollapsed(total);
    const contId = containerContasId(total.id);

    return (
      <div>
        {!(globalSearch ? false : collapsed) && (
          <DroppableList id={contId} style={{ display: "grid", gap: 8 }}>
            <SortableContext
              items={contas.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              {contas.length === 0 && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Sem contas"
                />
              )}
              {contas.map((c) => {
                if (globalSearch && !visibleNodes.has(c.id)) return null;
                return (
                  <SortableCard
                    key={c.id}
                    id={c.id}
                    dragDisabled={dreConfiguratorReadOnly}
                    className="node-conta"
                    bodyStyle={{ padding: 8 }}
                    style={{
                      backgroundColor: theme.backgroundSecondary,
                      border: `1px solid ${theme.borderSecondary}`,
                    }}
                  >
                    {renderNodeHeader({
                      node: c,
                      tag: { label: "Conta", color: "geekblue" },
                      collapsible: true,
                      collapsed: globalSearch ? false : isContaCollapsed(c),
                      onToggle: () => toggleConta(c),
                      childCount: subsByConta.get(c.codDRE)?.length || 0,
                    })}
                    <div style={{ marginTop: 8 }}>{renderSubContas(c)}</div>
                  </SortableCard>
                );
              })}
            </SortableContext>
          </DroppableList>
        )}
      </div>
    );
  }

  // ----------------------- DnD handlers -----------------------
  const [activeId, setActiveId] = useState(null);

  function onDragStart(event) {
    setActiveId(event.active.id);
  }

  function onDragCancel() {
    setActiveId(null);
  }

  function onDragEnd(event) {
    if (dreConfiguratorReadOnly) {
      setActiveId(null);
      return;
    }
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    if (
      String(active.id).startsWith("cat:") ||
      String(active.id).startsWith("catgroup:")
    ) {
      const overId = over.id;
      const activeDragId = String(active.id);
      let targetCatIds = availableTreeData.dragTargetIdsByDragId.get(activeDragId) || [];

      if (String(active.id).startsWith("cat:") && targetCatIds.length === 0) {
        const catId = String(active.id).split(":")[1];
        const isAssociable = !!catAssociationRules.associableById.get(catId);
        if (isAssociable) targetCatIds = [catId];
      }

      const targetIdsSet = new Set(targetCatIds.map((id) => String(id)));
      const resolveTargetSubId = () => {
        if (String(overId).startsWith("catsub:")) {
          return String(overId).split(":")[1];
        }
        if (subsById.has(overId)) {
          return String(overId);
        }
        if (String(overId).startsWith("cat:")) {
          const overCatId = String(overId).split(":")[1];
          const overCat = cats.find((cat) => String(cat.id) === overCatId);
          const subId = overCat?.syncDREId;
          if (subId && subId !== ZERO_GUID) {
            return String(subId);
          }
        }
        return null;
      };
      const targetSubId = resolveTargetSubId();

      if (targetSubId && targetIdsSet.size === 0) {
        message.warning("Nenhuma categoria visível disponível para associar.");
        return;
      }

      setCats((prevCats) =>
        prevCats.map((c) => {
          if (!targetIdsSet.has(String(c.id))) return c;
          if (overId === CATS_UNLINKED) {
            if (c.syncDREId === ZERO_GUID) return c;
            return { ...c, syncDREId: ZERO_GUID, isDirty: true };
          }
          if (targetSubId) {
            if (c.syncDREId === targetSubId) return c;
            return { ...c, syncDREId: targetSubId, isDirty: true };
          }
          return c;
        }),
      );
      return;
    }

    const activeType = getItemType(active.id);
    const overType = getItemType(over.id);

    if (activeType === "conta") {
      if (
        overType === "sub" ||
        (isContainerId(over.id) && String(over.id).startsWith("subs:"))
      ) {
        return;
      }
    }
    if (activeType === "sub") {
      if (
        overType === "total" ||
        over.id === CONTAINER_TOTALS ||
        (isContainerId(over.id) && String(over.id).startsWith("contas:"))
      ) {
        return;
      }
    }

    // ---------- Reordenar Totalizadores ----------
    if (activeType === "total") {
      const orderIds = totals.map((t) => t.id);
      const oldIndex = orderIds.indexOf(active.id);
      let newIndex;

      if (overType === "total") {
        const overIndex = orderIds.indexOf(over.id);
        newIndex = overIndex;
      } else if (over.id === CONTAINER_TOTALS) {
        newIndex = orderIds.length - 1;
      } else {
        return;
      }

      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;


      const newOrderIds = arrayMove(orderIds, oldIndex, newIndex);
      const draft = clone(nodes);
      newOrderIds.forEach((id, idx) => {
        const t = draft.find((x) => x.id === id);
        if (t && isTotalizador(t)) t.position = idx + 1;
      });

      // Recalcula códigos dos totalizadores e atualiza todos os filhos
      reindexTotalsCascade(draft);
      repairParentPointers(draft);


      setNodes(draft);
      return;
    }

    // ---------- Contas: mover/ordenar entre Totalizadores ----------
    if (activeType === "conta") {
      const draft = clone(nodes);
      const conta = draft.find((n) => n.id === active.id);
      if (!conta) return;

      const fromTotal = getContaParentTotal(conta);
      let toTotal = null;
      let targetIds = [];

      if (overType === "conta") {
        const overConta = draft.find((n) => n.id === over.id);
        if (!overConta) return;
        toTotal = getContaParentTotal(overConta);
        targetIds = draft
          .filter((n) => isConta(n) && n.codDRESup === toTotal.codDRE)
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .map((n) => n.id);
      } else if (
        isContainerId(over.id) &&
        String(over.id).startsWith("contas:")
      ) {
        const targetTotalId = String(over.id).split(":")[1];
        toTotal = draft.find((n) => isTotalizador(n) && n.id === targetTotalId);
        targetIds = draft
          .filter((n) => isConta(n) && n.codDRESup === toTotal.codDRE)
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .map((n) => n.id);
      } else {
        return;
      }

      const fromIds = draft
        .filter((n) => isConta(n) && n.codDRESup === fromTotal.codDRE)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((n) => n.id);

      const oldIndex = fromIds.indexOf(active.id);
      const newIndex =
        overType === "conta" ? targetIds.indexOf(over.id) : targetIds.length;

      if (!fromTotal || !toTotal) return;

      if (fromTotal.id !== toTotal.id) {
        // Coleta todas as subcontas da conta que será movida ANTES de movê-la
        const contaToMove = draft.find((n) => n.id === active.id);
        const subContasToMove = draft.filter(
          (n) =>
            !n.isDeleted && isSubConta(n) && n.codDRESup === contaToMove.codDRE,
        );


        // move para o novo total
        moveContaBetweenTotals(draft, active.id, toTotal);

        // reposiciona no destino
        const targetList = draft
          .filter((n) => isConta(n) && n.codDRESup === toTotal.codDRE)
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        const idxInTarget = targetList.findIndex((x) => x.id === active.id);
        const reordered = arrayMove(targetList, idxInTarget, newIndex);
        reordered.forEach((c, i) => (c.position = i + 1));

        // ——— recálculos com mapa old->new para NÃO perder subcontas ———
        recalcContasUnderTotal(draft, toTotal); // usa a versão segura acima
        recalcContasUnderTotal(draft, fromTotal); // idem, para renumeração do total de origem

        // após o recalc do destino, a Conta movida já tem um novo codDRE
        const movedConta = draft.find((n) => n.id === active.id);
        if (movedConta) {

          // Atualiza as subcontas da conta movida para o novo código
          for (const sub of subContasToMove) {
            sub.codDRESup = movedConta.codDRE;
            sub.isDirty = true;
          }
          // e numera as SubContas dessa conta pelo novo prefixo
          recalcSubsUnderConta(draft, movedConta);
        }
      } else {
        // reordenação dentro do mesmo total
        const sameList = draft
          .filter((n) => isConta(n) && n.codDRESup === fromTotal.codDRE)
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        const reordered = arrayMove(sameList, oldIndex, newIndex);
        reordered.forEach((c, i) => (c.position = i + 1));

        // versão segura que também remapeia subcontas dos irmãos
        recalcContasUnderTotal(draft, fromTotal);
      }

      repairParentPointers(draft);
      setNodes(draft);
      return;
    }

    // ---------- SubContas: mover/ordenar entre Contas ----------
    if (activeType === "sub") {
      const draft = clone(nodes);
      const sub = draft.find((n) => n.id === active.id);
      if (!sub) return;
      const fromConta = getSubParentConta(sub);
      let toConta = null;
      let targetIds = [];

      if (overType === "sub") {
        const overSub = draft.find((n) => n.id === over.id);
        if (!overSub) return;
        toConta = getSubParentConta(overSub);
        targetIds = draft
          .filter((n) => isSubConta(n) && n.codDRESup === toConta.codDRE)
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .map((n) => n.id);
      } else if (
        isContainerId(over.id) &&
        String(over.id).startsWith("subs:")
      ) {
        const targetContaCode = String(over.id).split(":")[1];
        toConta = draft.find((n) => isConta(n) && n.codDRE === targetContaCode);
        targetIds = draft
          .filter((n) => isSubConta(n) && n.codDRESup === targetContaCode)
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .map((n) => n.id);
      } else {
        return;
      }

      const fromIds = draft
        .filter((n) => isSubConta(n) && n.codDRESup === fromConta.codDRE)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((n) => n.id);

      const oldIndex = fromIds.indexOf(active.id);
      const newIndex =
        overType === "sub" ? targetIds.indexOf(over.id) : targetIds.length;

      if (!fromConta || !toConta) return;

      if (fromConta.id !== toConta.id) {
        moveSubBetweenContas(draft, active.id, toConta);
        // Reordena no destino
        const targetList = draft
          .filter((n) => isSubConta(n) && n.codDRESup === toConta.codDRE)
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        const idxInTarget = targetList.findIndex((x) => x.id === active.id);
        const reordered = arrayMove(targetList, idxInTarget, newIndex);
        reordered.forEach((s, i) => (s.position = i + 1));
        recalcSubsUnderConta(draft, toConta);
        // Ajusta origem
        recalcSubsUnderConta(draft, fromConta);
      } else {
        const sameList = draft
          .filter((n) => isSubConta(n) && n.codDRESup === fromConta.codDRE)
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        const reordered = arrayMove(sameList, oldIndex, newIndex);
        reordered.forEach((s, i) => (s.position = i + 1));
        recalcSubsUnderConta(draft, fromConta);
      }

      repairParentPointers(draft);
      setNodes(draft);
      return;
    }
  }

  function clearCatsForSubIdsAndSet(draftNodes, subIds) {
    setCats((prevCats) => {
      const nextCats = clearCatsForSubIds(prevCats, subIds);
      setNodes(draftNodes);
      return nextCats;
    });
  }

  // handleApplyChanges removido - responsabilidade passou para o pai

  // ----------------------- Render principal -----------------------
  const isExcel = syncInfo?.erp === 3;
  const hasNoNodes = !nodes || nodes.length === 0;

  const availableTreeData = useMemo(() => {
    const compareCats = (a, b) =>
      String(a.codCategoria).localeCompare(String(b.codCategoria), "pt-BR", {
        numeric: true,
      });

    const unlinkedCats = cats.filter(isUnlinkedCat);
    const nodeById = new Map();
    const childrenByParentId = new Map();
    const catIdByCode = new Map();

    unlinkedCats.forEach((cat) => {
      const id = String(cat.id);
      nodeById.set(id, { cat, children: [] });
      const code = catAssociationRules.catCodeById.get(id);
      if (code && !catIdByCode.has(code)) {
        catIdByCode.set(code, id);
      }
    });

    const rootIds = [];
    unlinkedCats.forEach((cat) => {
      const id = String(cat.id);
      const parentCode = catAssociationRules.parentCodeById.get(id);
      if (!parentCode || parentCode === ZERO_GUID) {
        rootIds.push(id);
        return;
      }

      const parentId = catIdByCode.get(parentCode);
      if (!parentId || parentId === id) {
        rootIds.push(id);
        return;
      }

      if (!childrenByParentId.has(parentId)) {
        childrenByParentId.set(parentId, []);
      }
      childrenByParentId.get(parentId).push(id);
    });

    childrenByParentId.forEach((childIds, parentId) => {
      const parentNode = nodeById.get(parentId);
      if (!parentNode) return;
      parentNode.children = childIds
        .map((childId) => nodeById.get(childId))
        .filter(Boolean)
        .sort((a, b) => compareCats(a.cat, b.cat));
    });

    const sortTree = (nodes) => {
      nodes.sort((a, b) => compareCats(a.cat, b.cat));
      nodes.forEach((node) => sortTree(node.children));
    };

    const roots = Array.from(new Set(rootIds))
      .map((id) => nodeById.get(id))
      .filter(Boolean);
    sortTree(roots);

    const normalizedSearch = String(globalSearch || "").trim().toLowerCase();
    const includesTerm = (cat) =>
      !normalizedSearch ||
      String(cat.descricao || "").toLowerCase().includes(normalizedSearch) ||
      String(cat.codCategoria || "").toLowerCase().includes(normalizedSearch);

    const uniqueIds = (ids) => Array.from(new Set(ids.map((id) => String(id))));
    const filterNode = (node) => {
      const id = String(node.cat.id);
      const isSelfAssociable = !!catAssociationRules.associableById.get(id);
      const childResults = node.children.map(filterNode).filter(Boolean);
      const childNodes = childResults.map((result) => result.node);
      const childVisibleAssociableIds = uniqueIds(
        childResults.flatMap((result) => result.visibleAssociableIds),
      );
      const selfMatches = includesTerm(node.cat);

      // Não associáveis (pais/root) só aparecem quando têm filho visível.
      if (!isSelfAssociable && childNodes.length === 0) {
        return null;
      }

      if (isSelfAssociable && !selfMatches) {
        return null;
      }

      const dragCatIds = isSelfAssociable ? [id] : childVisibleAssociableIds;
      const dragId = dragCatIds.length > 0
        ? isSelfAssociable
          ? `cat:${id}`
          : `catgroup:${id}`
        : null;

      return {
        node: {
          ...node,
          children: childNodes,
          dnd: {
            dragId,
            dragCatIds,
            isSelfAssociable,
          },
        },
        visibleAssociableIds: uniqueIds(
          isSelfAssociable ? [id] : childVisibleAssociableIds,
        ),
      };
    };

    const filteredRoots = roots.map(filterNode).filter(Boolean).map((result) => result.node);
    const draggableIds = [];
    const dragTargetIdsByDragId = new Map();
    const walk = (node) => {
      const dragId = node?.dnd?.dragId;
      const dragCatIds = node?.dnd?.dragCatIds || [];
      if (dragId && dragCatIds.length > 0) {
        draggableIds.push(dragId);
        dragTargetIdsByDragId.set(dragId, dragCatIds);
      }
      node.children.forEach(walk);
    };
    filteredRoots.forEach(walk);

    const associableUnlinkedCount = unlinkedCats.filter((cat) =>
      catAssociationRules.associableById.get(String(cat.id)),
    ).length;

    return {
      roots: filteredRoots,
      draggableIds,
      dragTargetIdsByDragId,
      associableUnlinkedCount,
    };
  }, [catAssociationRules, cats, globalSearch, isUnlinkedCat]);

  // Calculate counts for badges
  const linkedCatsCount = cats.filter(
    (c) =>
      c.syncDREId !== null &&
      c.syncDREId !== undefined &&
      c.syncDREId !== ZERO_GUID,
  ).length;
  const availableCatsCount = availableTreeData.associableUnlinkedCount;

  // Filter tree nodes based on search
  const visibleNodes = useMemo(() => {
    if (!globalSearch) return new Set(nodes.map((n) => n.id));

    const search = globalSearch.toLowerCase();
    const visible = new Set();

    // Categorias que dão match
    const matchingCats = cats.filter(
      (c) =>
        c.descricao.toLowerCase().includes(search) ||
        String(c.codCategoria).includes(search),
    );
    const matchingCatSyncIds = new Set(
      matchingCats
        .map((c) => c.syncDREId)
        .filter((id) => id && id !== ZERO_GUID),
    );

    // 1. Marcar nós que dão match direto
    nodes.forEach((n) => {
      const matchName = n.descricao.toLowerCase().includes(search);
      const matchCat = matchingCatSyncIds.has(n.id);
      if (matchName || matchCat) {
        visible.add(n.id);

        // Marcar ancestrais
        let current = n;
        while (current) {
          visible.add(current.id);
          if (isSubConta(current)) {
            current = nodes.find(
              (p) =>
                isConta(p) && String(p.codDRE) === String(current.codDRESup),
            );
          } else if (isConta(current)) {
            current = nodes.find(
              (p) =>
                isTotalizador(p) &&
                String(p.codDRE) === String(current.codDRESup),
            );
          } else {
            current = null;
          }
        }
      }
    });

    return visible;
  }, [globalSearch, nodes, cats]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  const renderAvailableCatNode = (node, level = 0) => {
    const c = node.cat;
    const catId = String(c.id);
    const isAssociable = !!node?.dnd?.isSelfAssociable;
    const dragId = node?.dnd?.dragId;
    const dragCatIds = node?.dnd?.dragCatIds || [];
    const isDraggable = !!dragId && dragCatIds.length > 0;
    const isGroupDrag = !isAssociable && isDraggable;
    const code = catAssociationRules.catCodeById.get(catId);
    const parentCode = catAssociationRules.parentCodeById.get(catId);
    const isRoot = parentCode === ZERO_GUID;
    const isParent = !!code && catAssociationRules.parentCodes.has(code);
    const hasChildren = node.children.length > 0;

    const catBody = (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          paddingLeft: `${level * 18}px`,
          width: "100%",
          overflow: "hidden",
        }}
      >
        {isDraggable ? (
          <HolderOutlined
            style={{ fontSize: 14, color: theme.textSecondary, cursor: "grab" }}
          />
        ) : !isAssociable ? (
          <Tag style={{ margin: 0, fontSize: 10 }} color="default">
            Bloqueado
          </Tag>
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 4,
              gap: 6,
            }}
          >
            <Space size={4} wrap>
              {c.natureza && (
                <Tag
                  color={String(c.natureza).toUpperCase() === "RECEITA" ? "blue" : "red"}
                  style={{ fontSize: 10, margin: 0 }}
                >
                  {c.natureza}
                </Tag>
              )}
              {isGroupDrag && (
                <Tag style={{ fontSize: 10, margin: 0 }} color="processing">
                  Lote ({dragCatIds.length})
                </Tag>
              )}
              {!isDraggable && !isAssociable && isRoot && (
                <Tag style={{ fontSize: 10, margin: 0 }} color="orange">
                  Root
                </Tag>
              )}
              {!isDraggable && !isAssociable && isParent && (
                <Tag style={{ fontSize: 10, margin: 0 }} color="gold">
                  Pai
                </Tag>
              )}
              {hasChildren && (
                <Tag style={{ fontSize: 10, margin: 0 }} color="default">
                  Grupo
                </Tag>
              )}
            </Space>
          </div>
          <Text
            style={{
              fontSize: 12,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "block",
              color: theme.textPrimary,
              opacity: isDraggable || isAssociable ? 1 : 0.75,
            }}
            title={c.descricao}
          >
            {c.descricao}
          </Text>
        </div>
      </div>
    );

    return (
      <div key={catId} style={{ display: "grid", gap: 6, width: "100%" }}>
        {isDraggable ? (
          <SortableCard
            id={dragId}
            dragDisabled={dreConfiguratorReadOnly}
            className="cat-chip"
            bodyStyle={{ padding: "8px 12px" }}
            style={{
              backgroundColor: theme.cardBackground,
              border: `1px solid ${theme.borderSecondary}`,
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            {catBody}
          </SortableCard>
        ) : (
          <div
            style={{
              padding: "8px 12px",
              backgroundColor: theme.backgroundSecondary,
              border: `1px dashed ${theme.borderSecondary}`,
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            {catBody}
          </div>
        )}
        {node.children.map((child) => renderAvailableCatNode(child, level + 1))}
      </div>
    );
  };

  const renderDreStructure = () => (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        flexDirection: isNarrow ? "column" : "row",
        flexWrap: "nowrap",
        gap: 16,
        alignItems: "flex-start",
        height: isNarrow ? "calc(100vh - 140px)" : "calc(100vh - 220px)",
      }}
    >
      {/* Coluna esquerda: Categorias disponíveis */}
      <Card
        size="small"
        title={
          <Space>
            <Text strong>CATEGORIAS DISPONIVEIS</Text>
            <Tooltip title="Total de categorias folhas associáveis">
              <Badge
                count={availableCatsCount}
                style={{ backgroundColor: "#108ee9" }}
              />
            </Tooltip>
          </Space>
        }
        style={{
          flexGrow: 0,
          flexShrink: 1,
          flexBasis: isNarrow ? 140 : "20%",
          width: isNarrow ? "100%" : "20%",
          display: "flex",
          flexDirection: "column",
          height: isNarrow ? undefined : "100%",
          overflow: "hidden",
        }}
        styles={{
          body: {
            flex: 1,
            overflowY: "auto",
            padding: 8,
            backgroundColor: theme.backgroundSecondary,
          },
        }}
      >
        <DroppableList
          id={CATS_UNLINKED}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            minHeight: "100%",
          }}
        >
          <SortableContext
            items={availableTreeData.draggableIds}
            strategy={verticalListSortingStrategy}
          >
            {availableTreeData.roots.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Nenhuma categoria disponível"
              />
            ) : (
              availableTreeData.roots.map((node) => renderAvailableCatNode(node, 0))
            )}
          </SortableContext>
        </DroppableList>
      </Card>

      {/* Coluna direita: Estrutura do DRE */}
      <Card
        size="small"
        title={
          <Space>
            <Text strong>Estrutura do DRE</Text>
            <Badge count={linkedCatsCount} showZero color="#52c41a" />
          </Space>
        }
        extra={
          <Space>
            {isExcel && (
              <Button
                size="small"
                onClick={handleCreateInitialDreConfig}
                loading={loading}
                danger={!hasNoNodes}
                disabled={dreConfiguratorReadOnly}
              >
                {hasNoNodes ? "Criar Config Inicial" : "Resetar"}
              </Button>
            )}
            <Tooltip title={areAllCollapsed ? "Expandir Tudo" : "Colapsar Tudo"}>
              <Button
                size="small"
                icon={areAllCollapsed ? <ExpandOutlined /> : <CompressOutlined />}
                onClick={handleToggleExpandAll}
              />
            </Tooltip>
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={addTotalizador}
              disabled={dreConfiguratorReadOnly}
            >
              Totalizador
            </Button>
          </Space>
        }
        style={{
          flexGrow: isNarrow ? 1 : 0,
          flexShrink: 1,
          flexBasis: isNarrow ? 0 : "80%",
          width: isNarrow ? "100%" : "80%",
          display: "flex",
          flexDirection: "column",
          height: isNarrow ? undefined : "100%",
          overflow: "hidden",
        }}
        styles={{
          body: {
            flex: 1,
            overflowY: "auto",
            padding: 12,
            backgroundColor: theme.cardBackground,
          },
        }}
      >
        <DroppableList
          id={CONTAINER_TOTALS}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            minHeight: "100%",
            paddingBottom: 40,
          }}
        >
          <SortableContext
            items={totals.map((t) => t.id)}
            strategy={verticalListSortingStrategy}
          >
            {totals.length === 0 && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Sem totalizadores. Adicione um para começar."
              />
            )}
            {totals.map((t) => {
              if (globalSearch && !visibleNodes.has(t.id)) return null;
              return (
                <SortableCard
                  key={t.id}
                  id={t.id}
                  dragDisabled={dreConfiguratorReadOnly}
                  className="node-total"
                  bodyStyle={{ padding: 0 }}
                  style={{
                    backgroundColor: theme.cardBackground,
                    border: `1px solid ${theme.borderSecondary}`,
                    borderRadius: 6,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: 12,
                      backgroundColor: theme.backgroundSecondary,
                      borderBottom: !(globalSearch
                        ? false
                        : isTotalCollapsed(t))
                        ? `1px solid ${theme.borderSecondary}`
                        : "none",
                    }}
                  >
                    {renderNodeHeader({
                      node: t,
                      tag: { label: "TOTAL", color: "purple" },
                      collapsible: true,
                      collapsed: globalSearch ? false : isTotalCollapsed(t),
                      onToggle: () => toggleTotal(t),
                      childCount: contasByTotal.get(t.id)?.length || 0,
                    })}
                  </div>
                  {!(globalSearch ? false : isTotalCollapsed(t)) && (
                    <div style={{ padding: "12px 12px 12px 32px" }}>
                      {renderContas(t)}
                    </div>
                  )}
                </SortableCard>
              );
            })}
          </SortableContext>
        </DroppableList>
      </Card>
    </div>
  );

  const renderIndicatorsLayout = () => {
    const getSegmentedLabel = (label, value, icon = null) => {
      const isSelected = activeIndicatorSubMenu === value;
      return (
        <div
          style={{
            padding: "4px 8px",
            color: isSelected ? undefined : theme.textSecondary,
            backgroundColor: isSelected ? undefined : theme.borderSecondary,
            border: isSelected ? '1px solid' : null,
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            opacity: isSelected ? 1 : 0.85,
          }}
        >
          {icon}
          {label}
        </div>
      );
    };

    return (
      <Card
        style={{
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${theme.borderSecondary}`,
          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.01)",
        }}
        styles={{
          body: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <div
          style={{
            padding: "16px 24px",
            borderBottom: `1px solid ${theme.borderSecondary}`,
          }}
        >
          <Segmented
            block
            options={[
              {
                label: getSegmentedLabel("% Indicadores DRE", "financial"),
                value: "financial",
              },
              {
                label: getSegmentedLabel(
                  "Indicadores KPI",
                  "kpi",
                  <TagOutlined />,
                ),
                value: "kpi",
              },
              {
                label: getSegmentedLabel(
                  "Grupos Financeiros",
                  "financial_groups",
                  <TagOutlined />,
                ),
                value: "financial_groups",
              },
            ]}
            value={activeIndicatorSubMenu}
            onChange={handleSubMenuChange}
          />
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "24px",
          }}
        >
          {activeIndicatorSubMenu === "financial" &&
            (indicatorsContent || (
              <div style={{ textAlign: "center", padding: 24, color: theme.textSecondary }}>
                <Empty description="Configuração de indicadores em breve" />
              </div>
            ))}
          {activeIndicatorSubMenu === "kpi" &&
            (kpiContent || (
              <div style={{ textAlign: "center", padding: 24, color: theme.textSecondary }}>
                <Empty description="Configuração de KPI em breve" />
              </div>
            ))}
          {activeIndicatorSubMenu === "financial_groups" &&
            (financialGroupsContent || (
              <div style={{ textAlign: "center", padding: 24, color: theme.textSecondary }}>
                <Empty description="Configuração de Grupos Financeiros em breve" />
              </div>
            ))}
        </div>
      </Card>
    );
  };

  const getMainSegmentedLabel = (label, value, icon = null) => {
    const isSelected = activeTab === value;
    return (
      <div
        style={{
          padding: "4px 8px",
          color: isSelected ? undefined : theme.textSecondary,
          backgroundColor: isSelected ? undefined : theme.borderSecondary,
          border: isSelected ? '1px solid' : null,
          borderRadius: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          opacity: 1,
        }}
      >
        {icon}
        {label}
      </div>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <style>
        {`
          :where(.css-dev-only-do-not-override-2ihwg1).ant-segmented .ant-segmented-item-label,
          .ant-segmented .ant-segmented-item-label {
            padding: 0 !important;
            border-radius: 4px !important;
          }
        `}
      </style>
      {
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 0",
            backgroundColor: theme.background,
            borderRadius: 8,
            marginBottom: 16,
            gap: 16,
          }}
        >
          <Input
            placeholder="Pesquisar categorias..."
            prefix={
              <SearchOutlined
                style={{ color: theme.textSecondary, fontSize: 18, marginRight: 8 }}
              />
            }
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            style={{
              flex: 1,
              backgroundColor: theme.inputBackground,
              color: theme.textPrimary,
              borderColor: theme.border,
              height: 48,
              fontSize: 16,
              borderRadius: 8,
            }}
            allowClear
          />
          <Button
            type="primary"
            loading={isSaving}
            onClick={handleSaveAllChanges}
            style={{
              height: 48,
              padding: "0 32px",
              fontSize: 16,
              fontWeight: 600,
              borderRadius: 8,
              boxShadow: "0 4px 14px 0 rgba(0, 0, 0, 0.39)",
            }}
          >
            Salvar Alterações
          </Button>
        </div>
      }

      <div>
        <Segmented
          block
          options={[
            {
              label: getMainSegmentedLabel(
                "Estrutura",
                "Structure",
                <UnorderedListOutlined />,
              ),
              value: "Structure",
            },
            {
              label: getMainSegmentedLabel("% Indicadores", "Indicators"),
              value: "Indicators",
            },
          ]}
          value={activeTab}
          onChange={handleTabChange}
        />
      </div>

      <div style={{ flex: 1, overflow: "hidden" }}>
        {activeTab === "Structure" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%" }}>
            {supportsErpDefaultDre && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  padding: "4px 0 0",
                }}
              >
                <Space align="center" size={12}>
                  <Switch
                    checked={!!useErpDreFlag}
                    onChange={handleUseErpDreConfigChange}
                    loading={toggleErpDreSaving}
                  />
                  <Text style={{ color: theme.textPrimary }}>
                    { currentErp == 0 ? "Utilizar modelo internacional de DRE" : "Utilizar estrutura padrão ERP" }
                  </Text>
                </Space>
                {!!useErpDreFlag && (
                  <Text style={{ color: theme.textSecondary, fontSize: 13 }}>
                    {currentErp == 0 ? "A estrutura do DRE seguirá o modelo internacional e não poderá ser editada aqui." : "A estrutura do DRE seguirá o " + erpDefaultDreLabel + " e não poderá ser editada aqui."}
                  </Text>
                )}
              </div>
            )}
            <Card
              size="small"
              title={
                <Space size={8} align="center">
                  <TagOutlined />
                  <Text strong style={{ color: theme.textPrimary }}>
                    Análise Vertical (Base 100%)
                  </Text>
                </Space>
              }
              style={{
                backgroundColor: theme.background,
                borderColor: theme.border,
              }}
              styles={{
                body: {
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                },
              }}
            >
              <Text style={{ color: theme.textSecondary, fontSize: 13 }}>
                Selecione qual totalizador/conta será usado como 100%. Se deixar
                padrão, o primeiro totalizador do DRE será considerado como 100%.
              </Text>
              <Select
                showSearch
                autoClearSearchValue={false}
                placeholder="Selecione um totalizador ou conta..."
                value={pendingBaseAvDreId ?? AV_BASE_DEFAULT_VALUE}
                options={avBaseSelectOptions}
                optionFilterProp="label"
                onChange={handleBaseAvSelectionChange}
                style={{ width: "100%" }}
              />
            </Card>

            {isOmie && (
              <Card
                size="small"
                title={
                  <Space size={8} align="center">
                    <TagOutlined />
                    <Text strong style={{ color: theme.textPrimary }}>
                      Regra DRE por pagamento
                    </Text>
                  </Space>
                }
                style={{
                  backgroundColor: theme.background,
                  borderColor: theme.border,
                }}
                styles={{
                  body: {
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  },
                }}
              >
                <Select
                  value={tipoDrePag}
                  options={TIPO_DRE_PAGAMENTO_OPTIONS}
                  onChange={handleTipoDrePagChange}
                  style={{ width: "100%" }}
                />
                <Space size={6} align="start">
                  <Text style={{ color: theme.textSecondary, fontSize: 13 }}>
                    {getTipoDrePagamentoDescricao(tipoDrePag)}
                  </Text>
                  <Tooltip
                    color="#ffffff"
                    title={renderExemploDre(getTipoDrePagamentoExemplo(tipoDrePag))}
                  >
                    <InfoCircleOutlined
                      style={{
                        color: theme.textSecondary,
                        fontSize: 14,
                        cursor: "pointer",
                        marginTop: 2,
                      }}
                    />
                  </Tooltip>
                </Space>
              </Card>
            )}

            <CategoriasComLancamentoSemDre syncId={syncId} refreshToken={dreAlertRefresh} />

            {dreConfiguratorReadOnly ? (
              isContaAzulV2 && (
                <DefaultFinancialCategoriesConfig
                  syncId={syncId}
                  ref={defaultFinancialRef}
                  onConfigChange={setDefaultFinancialConfig}
                />
              )
            ) : (
              <div
                style={{
                  flex: 1,
                  overflow: "hidden",
                }}
              >
              <DndContext
                sensors={sensors}
                collisionDetection={(args) => {
                  const pointer = pointerWithin(args);
                  return pointer.length > 0 ? pointer : rectIntersection(args);
                }}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onDragCancel={onDragCancel}
              >
                {renderDreStructure()}

                <DragOverlay
                  dropAnimation={{
                    duration: 250,
                    easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
                  }}
                >
                  {activeId
                    ? (() => {
                        const n = nodes.find((x) => x.id === activeId);

                    // Base styles for better visibility during drag
                        const baseStyle = {
                          boxShadow:
                            "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
                          opacity: 0.9,
                          cursor: "grabbing",
                          transform: "scale(1.02)",
                        };

                    // Handling Category Drag Preview
                        if (String(activeId).startsWith("catgroup:")) {
                          const groupCatId = String(activeId).split(":")[1];
                          const groupCat = cats.find(
                            (c) => String(c.id) === groupCatId,
                          );
                          const batchIds =
                            availableTreeData.dragTargetIdsByDragId.get(
                              String(activeId),
                            ) || [];
                          if (groupCat) {
                            return (
                              <SortableCard
                                key={`catgroup:${groupCat.id}`}
                                id={`catgroup:${groupCat.id}`}
                                className="cat-chip"
                                bodyStyle={{ padding: "8px 12px" }}
                                style={{
                                  ...baseStyle,
                                  backgroundColor: theme.cardBackground,
                                  border: `1px solid ${theme.primary}`,
                                  borderRadius: 4,
                                  width: 320,
                                }}
                              >
                                <Space size={8} align="center">
                                  <HolderOutlined
                                    style={{
                                      fontSize: 14,
                                      color: theme.textSecondary,
                                    }}
                                  />
                                  <Tag color="processing" style={{ margin: 0 }}>
                                Lote ({batchIds.length})
                              </Tag>
                              <Text
                                style={{
                                  fontSize: 12,
                                  maxWidth: 220,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  color: theme.textPrimary,
                                }}
                              >
                                {groupCat.descricao}
                              </Text>
                            </Space>
                          </SortableCard>
                        );
                      }
                    }

                    if (String(activeId).startsWith("cat:")) {
                      const catId = String(activeId).split(":")[1];
                      const cat = cats.find((c) => String(c.id) === catId);
                      if (cat) {
                        return (
                          <SortableCard
                            key={`cat:${cat.id}`}
                            id={`cat:${cat.id}`}
                            className="cat-chip"
                            bodyStyle={{ padding: "8px 12px" }}
                            style={{
                              ...baseStyle,
                              backgroundColor: theme.cardBackground,
                              border: `1px solid ${theme.primary}`,
                              borderRadius: 4,
                              width: 280,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <HolderOutlined
                                style={{ fontSize: 14, color: theme.textSecondary }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 4,
                                  }}
                                >
                                  {cat.natureza && (
                                    <Tag
                                      color={
                                        String(cat.natureza).toUpperCase() ===
                                        "RECEITA"
                                          ? "blue"
                                          : "red"
                                      }
                                      style={{ fontSize: 10, margin: 0 }}
                                    >
                                      {cat.natureza}
                                    </Tag>
                                  )}
                                </div>
                                <Text
                                  style={{
                                    fontSize: 12,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "block",
                                    color: theme.textPrimary,
                                  }}
                                >
                                  {cat.descricao}
                                </Text>
                              </div>
                            </div>
                          </SortableCard>
                        );
                      }
                    }

                    if (!n) return null;

                    if (isTotalizador(n)) {
                      return (
                        <Card
                          size="small"
                          styles={{ body: { padding: 12 } }}
                          style={{
                            ...baseStyle,
                            backgroundColor: theme.infoBackground, // Using info background for totalizer
                            border: `1px solid ${theme.primary}`,
                          }}
                        >
                          {renderNodeHeader({
                            node: n,
                            tag: { label: "Totalizador", color: "purple" },
                            childCount: contasByTotal.get(n.id)?.length || 0,
                          })}
                        </Card>
                      );
                    }
                    if (isConta(n)) {
                      return (
                        <Card
                          size="small"
                          styles={{ body: { padding: 8 } }}
                          style={{
                            ...baseStyle,
                            backgroundColor: theme.backgroundSecondary,
                            border: `1px solid ${theme.border}`,
                          }}
                        >
                          {renderNodeHeader({
                            node: n,
                            tag: { label: "Conta", color: "geekblue" },
                            childCount: subsByConta.get(n.codDRE)?.length || 0,
                          })}
                        </Card>
                      );
                    }
                    if (isSubConta(n)) {
                      return (
                        <Card
                          size="small"
                          styles={{ body: { padding: 8 } }}
                          style={{
                            ...baseStyle,
                            backgroundColor: theme.cardBackground,
                            border: `1px solid ${theme.borderSecondary}`,
                          }}
                        >
                          {renderNodeHeader({
                            node: n,
                            tag: { label: "SubConta", color: "blue" },
                            childCount:
                              cats.filter((c) => c.syncDREId === n.id).length,
                          })}
                        </Card>
                      );
                    }
                    return null;
                  })()
                : null}
            </DragOverlay>

            
          </DndContext>
              </div>
            )}
          </div>
        ) : (
          renderIndicatorsLayout()
        )}
      </div>
      <Modal
              title={`Adicionar Categorias em Lote - ${targetSubForBulkAdd?.descricao}`}
              open={isBulkAddModalOpen}
              onOk={handleBulkAddSave}
              onCancel={handleBulkAddClose}
              width={600}
              destroyOnClose
              okText="Salvar"
              cancelText="Cancelar"
            >
              {targetSubForBulkAdd && (
                <CatSelect
                  sub={targetSubForBulkAdd}
                  cats={cats}
                  theme={theme}
                  bulkSelectedCatIds={bulkSelectedCatIds}
                  setBulkSelectedCatIds={setBulkSelectedCatIds}
                />
              )}
              <div style={{ marginTop: 16 }}>
                <Text type="secondary" style={{ fontSize: 12, color: theme.textSecondary }}>
                  Selecione as categorias que deseja vincular a esta SubConta.
                  Categorias desmarcadas serão desvinculadas.
                </Text>
              </div>
            </Modal>
    </div>
  );
}
